import { notifyError } from "@/lib/notify-error";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  constructFilterFn,
  useTable,
  type ColumnDef,
  type ColumnFiltersState,
  type SortingState,
} from "@tanstack/react-table";
import { appTableFeatures } from "@/lib/table-features";
import { useConvex, useMutation } from "convex/react";
import { convexQuery } from "@convex-dev/react-query";
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  Columns2,
  Copy,
  Edit,
  Eye,
  Filter,
  GitBranchPlus,
  Loader2,
  Maximize2,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { AppLink } from "@/components/app-link";
import { ComparisonStatusBadge } from "@/components/comparison-status-badge";
import { DeleteConfirmationDialog } from "@/components/delete-confirmation-dialog";
import { PageHeader, PageHeaderSkeleton, type Crumb } from "@/components/layout";
import { ProcessingSpinner } from "@/components/processing-spinner";
import { PdfModalPipViewport } from "@/components/pdf-modal-pip";
import { CountBadge, DeltaBadge, RevisionBadge } from "@/components/semantic-badges";
import { TablePreview } from "@/components/table-preview";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MultiSelect, type MultiSelectSection } from "@/components/ui/multi-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { withExportExtension } from "@/lib/export-filenames";
import { ExportButton } from "@/features/exports/export-button";
import { ExportDialog, lastFileFromArtifact } from "@/features/exports/export-dialog";
import { ExportOptionCheckbox, ExportOptionGroup } from "@/features/exports/export-options";
import { exportTarget } from "@/features/exports/export-names";
import { useExportFilenames } from "@/features/exports/use-export-filenames";
import { getTableRowsPayload } from "@/lib/table-rows-cache";
import { toTitleCase } from "@/lib/formatting";
import { cn } from "@/lib/utils";
import { useRouteParam } from "@/lib/use-route-param";
import { useAppNavigate } from "@/lib/use-app-navigate";
import { ActionLogProvider, useRecordActionLog } from "@/hooks/use-action-log-session";
import { formatMeasurementValue, type MeasurementType } from "@/lib/geometry";
import {
  ChangeGroupNotesPanel,
  CompactTradeSelector,
  type RevPilotNote,
} from "@/features/rev-pilot/change-group-notes";
import { PdfChangeReviewView } from "@/features/rev-pilot/pdf-change-review";
import { RequiresCapability, useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";

export const Route = createFileRoute("/revPilot/$comparisonId")({
  component: ComparisonDetailPage,
});

interface NarrativeSection {
  schedule: string;
  title: string;
  body: string;
}

interface ChangeLogEntry {
  schedule: string;
  sheet_no: string;
  item_identifier: string;
  change_type: "Added" | "Modified" | "Removed";
  field_changed: string;
  old_value: string;
  new_value: string;
  source_reference: string;
}

interface ScheduleComparisonReport {
  reportKind?: "schedule";
  comparison_name: string;
  project_name: string;
  narrative: {
    overview: string;
    sections: NarrativeSection[];
  };
  change_log: ChangeLogEntry[];
  stats: {
    matchedTables: number;
    addedTables: number;
    removedTables: number;
    changedRows: number;
    addedRows: number;
    removedRows: number;
    modifiedRows: number;
  };
}

interface PlanSetComparisonReport {
  reportKind: "plan_set";
  comparisonName: string;
  projectName: string;
  generatedAt: number;
  summary: string;
  totals: {
    totalPages: number;
    changedPages: number;
    totalRegions: number;
  };
}

type ComparisonReport = ScheduleComparisonReport | PlanSetComparisonReport;
type ComparisonStatus = "extracting" | "processing" | "completed" | "failed";
type ComparisonKind = "schedule" | "plan_set";
type DeltaMeasurementDisplay = MeasurementType;
const DELTA_MEASUREMENT_OPTIONS: DeltaMeasurementDisplay[] = ["LF", "SQFT", "CF"];
type QuantityDeltaOptionalColumn = "drawing" | DeltaMeasurementDisplay;
const QUANTITY_DELTA_OPTIONAL_COLUMNS: QuantityDeltaOptionalColumn[] = ["drawing", ...DELTA_MEASUREMENT_OPTIONS];
const PDF_EXPORT_MEASUREMENT_COLUMNS = ["Count", "LF", "SQFT", "CF"] as const;

interface QuantityDeltaRow {
  key: string;
  drawing: string;
  itemType: string;
  previousQuantity: number;
  currentQuantity: number;
  delta: number;
  previousLf: number;
  currentLf: number;
  lfDelta: number;
  previousSqft: number;
  currentSqft: number;
  sqftDelta: number;
  previousCf: number;
  currentCf: number;
  cfDelta: number;
}

interface QuantityDeltaColumnSelectProps {
  selectedColumns: QuantityDeltaOptionalColumn[];
  onSelectedColumnsChange: (columns: QuantityDeltaOptionalColumn[]) => void;
}

interface QuantityDeltaTableProps {
  rows: QuantityDeltaRow[];
  measurementDisplays: DeltaMeasurementDisplay[];
  expanded?: boolean;
  sorting?: SortingState;
  onSortingChange?: Dispatch<SetStateAction<SortingState>>;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: Dispatch<SetStateAction<ColumnFiltersState>>;
  showDrawingColumn?: boolean;
}

const quantityDeltaMultiSelectFilter = constructFilterFn<typeof appTableFeatures, QuantityDeltaRow>({
  filter: (value, filterValue) => {
    const selectedValues = Array.isArray(filterValue)
      ? filterValue.filter((item): item is string => typeof item === "string")
      : [];

    return selectedValues.length === 0 || selectedValues.includes(String(value));
  },
});

type ExtractedTableRef = {
  _id: string;
  name: string;
  pageNumber: number;
  headers: string[];
  rowCount: number;
  rowsVersion: number;
  rowsFormat: "json" | "json.gz";
  rowsDownloadUrl: string | null;
};

type StorageQueryArgs =
  | {
      fileKey?: string;
    }
  | "skip";

type NarrativeSummaryNote = {
  _id: Id<"revPilotChangeGroupNotes"> | Id<"revPilotChangeNotes">;
  body: string;
  label: string;
  createdAt: number;
  updatedAt: number;
  noteKind: "group" | "change";
  changeGroupId?: Id<"revPilotChangeGroups">;
  changeGroup?: {
    pageNumber?: number;
    groupOrder?: number;
    included?: boolean;
  } | null;
  changes?: Array<{
    _id: Id<"revPilotChanges">;
    pageNumber: number;
    changeOrder: number;
    included: boolean;
  }>;
};

export function formatNarrativeCopy(title: string, body: string) {
  return `${title}\n\n${body}`;
}

function getNarrativeNoteSourceLabel(note: NarrativeSummaryNote) {
  if (note.noteKind === "change") {
    const changes = note.changes ?? [];
    const firstChange = changes[0];
    if (!firstChange) return "No linked changes";
    const firstLabel = `Page ${firstChange.pageNumber} · Change ${firstChange.changeOrder}`;
    return changes.length > 1 ? `${firstLabel} · +${changes.length - 1} more` : firstLabel;
  }
  return `Page ${note.changeGroup?.pageNumber ?? "—"} · Change ${note.changeGroup?.groupOrder ?? "—"}`;
}

function isNarrativeGroupNote(note: NarrativeSummaryNote): note is NarrativeSummaryNote & RevPilotNote & { noteKind: "group" } {
  return note.noteKind === "group" && Boolean(note.changeGroupId);
}

function isNarrativeChangeNote(note: NarrativeSummaryNote): note is NarrativeSummaryNote & { _id: Id<"revPilotChangeNotes">; noteKind: "change" } {
  return note.noteKind === "change";
}

function NarrativeChangeNoteEditForm({
  note,
  onSave,
  onCancel,
  isSaving,
}: {
  note: NarrativeSummaryNote & { _id: Id<"revPilotChangeNotes">; noteKind: "change" };
  onSave: (noteId: Id<"revPilotChangeNotes">, body: string, label: string) => Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
}) {
  const [body, setBody] = useState(note.body);
  const [label, setLabel] = useState(note.label);

  useEffect(() => {
    setBody(note.body);
    setLabel(note.label);
  }, [note]);

  return (
    <div className="space-y-3 p-4">
      <div className="space-y-1.5">
        <Label
          htmlFor={`narrative-note-trade-${note._id}`}
          className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
        >
          Trade/Label
        </Label>
        <CompactTradeSelector
          id={`narrative-note-trade-${note._id}`}
          value={label}
          onChange={setLabel}
          disabled={isSaving}
        />
      </div>
      <div className="space-y-1.5">
        <Label
          htmlFor={`narrative-note-description-${note._id}`}
          className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground"
        >
          Description
        </Label>
        <Textarea
          id={`narrative-note-description-${note._id}`}
          value={body}
          onChange={(event) => setBody(event.target.value)}
          disabled={isSaving}
          className="min-h-28 resize-none px-2.5 text-xs"
        />
      </div>
      <div className="flex justify-end gap-2 border-t pt-3">
        <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button type="button" size="sm" disabled={isSaving || !body.trim() || !label.trim()} onClick={() => void onSave(note._id, body, label)}>
          {isSaving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          Save Note
        </Button>
      </div>
    </div>
  );
}

/** Report JSON is produced by the Python service; normalize casing for display ("added" → "Added"). */
function formatChangeType(changeType: string) {
  return toTitleCase(changeType);
}

function normalizeComparisonKind(kind: string | undefined): ComparisonKind {
  return kind === "plan_set" ? "plan_set" : "schedule";
}

function isScheduleReport(report: ComparisonReport | null): report is ScheduleComparisonReport {
  if (!report || ("reportKind" in report && report.reportKind === "plan_set")) {
    return false;
  }

  return (
    typeof report === "object" &&
    "stats" in report &&
    "narrative" in report &&
    "change_log" in report
  );
}

function formatMeasurementDelta(value: number, measurementType: MeasurementType) {
  if (value === 0) {
    return formatMeasurementValue(0, measurementType);
  }
  const prefix = value > 0 ? "+" : "-";
  return `${prefix}${formatMeasurementValue(Math.abs(value), measurementType)}`;
}

function renderSortIcon(sortDirection: false | "asc" | "desc") {
  if (!sortDirection) {
    return <ArrowUpDown className="size-3.5 text-muted-foreground" />;
  }

  return sortDirection === "asc" ? (
    <ArrowUp className="size-3.5 text-primary" />
  ) : (
    <ArrowDown className="size-3.5 text-primary" />
  );
}

function getComparisonProcessingMessage(status: ComparisonStatus, kind: ComparisonKind) {
  if (status === "extracting") {
    return kind === "plan_set"
      ? "Preparing plan revisions for RevPilot..."
      : "Extracting schedule data for RevPilot...";
  }
  if (status === "processing") {
    return kind === "plan_set"
      ? "Detecting plan changes..."
      : "Comparing schedule revisions...";
  }
  return "Processing RevPilot comparison...";
}

export function PlanSetPageProgress({
  completed,
  total,
}: {
  completed: number;
  total: number;
}) {
  if (total <= 0) return null;
  return (
    <p className="text-center text-sm font-medium text-muted-foreground">
      {completed} of {total} pages completed
    </p>
  );
}

function RetryPlanSetComparisonButton({
  comparisonId,
  comparisonKind,
}: {
  comparisonId: Id<"revPilotScheduleComparisons">;
  comparisonKind: ComparisonKind;
}) {
  const retryPlanSetComparison = useMutation(
    api.planSetComparisonPages.retryFailedComparison
  );
  const [isRetrying, setIsRetrying] = useState(false);

  if (comparisonKind !== "plan_set") return null;

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      await retryPlanSetComparison({ comparisonId });
      toast.success("Retrying unfinished pages");
    } catch (error) {
      notifyError(error,
        error instanceof Error ? error.message : "Failed to retry comparison"
      );
    } finally {
      setIsRetrying(false);
    }
  };

  return (
    <RequiresCapability mode="disable">
      <Button
        variant="outline"
        className="mt-3"
        disabled={isRetrying}
        onClick={() => void handleRetry()}
      >
        {isRetrying ? <Loader2 className="size-4 animate-spin" /> : null}
        Retry unfinished pages
      </Button>
    </RequiresCapability>
  );
}

function isPlanSetReviewOpen(
  comparisonKind: ComparisonKind,
  comparisonStatus: ComparisonStatus | undefined,
  reviewStatus: string | undefined,
  showCompletedReviewChanges: boolean
) {
  return (
    comparisonKind === "plan_set" &&
    comparisonStatus === "completed" &&
    (showCompletedReviewChanges || reviewStatus === "pending" || reviewStatus === "in_progress")
  );
}

function buildStorageQueryArgs(
  fileKey?: string | null
): StorageQueryArgs {
  if (!fileKey) return "skip";
  return {
    fileKey: fileKey ?? undefined,
  };
}

function useComparisonReportUrls(comparison: {
  reportJsonFileKey?: string;
  reportPdfFileKey?: string;
  reportWorkbookFileKey?: string;
} | null) {
  const { data: reportJsonUrl, isPending: isReportJsonPending, isError: isReportJsonError } = useQuery(
    convexQuery(
      api.scheduleComparisons.getStorageUrl,
      buildStorageQueryArgs(comparison?.reportJsonFileKey)
    )
  );
  const { data: reportPdfUrl, isPending: isReportPdfPending, isError: isReportPdfError } = useQuery(
    convexQuery(
      api.scheduleComparisons.getStorageUrl,
      buildStorageQueryArgs(comparison?.reportPdfFileKey)
    )
  );
  const { data: reportWorkbookUrl, isPending: isReportWorkbookPending, isError: isReportWorkbookError } = useQuery(
    convexQuery(
      api.scheduleComparisons.getStorageUrl,
      buildStorageQueryArgs(comparison?.reportWorkbookFileKey)
    )
  );

  return {
    reportJsonUrl,
    reportPdfUrl,
    reportWorkbookUrl,
    isReportJsonPending,
    isReportPdfPending,
    isReportWorkbookPending,
    isReportJsonError,
    isReportPdfError,
    isReportWorkbookError,
  };
}

function useComparisonReport(
  comparisonStatus: ComparisonStatus | undefined,
  reportJsonUrl: string | null | undefined
) {
  const [report, setReport] = useState<ComparisonReport | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  useEffect(() => {
    if (comparisonStatus !== "completed" || !reportJsonUrl) {
      return;
    }
    const reportUrl = reportJsonUrl;

    const controller = new AbortController();

    async function loadReport() {
      setReportLoading(true);
      setReportError(null);
      try {
        const response = await fetch(reportUrl, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Failed to load report: ${response.status}`);
        }
        const payload = (await response.json()) as ComparisonReport;
        if (!controller.signal.aborted) {
          setReport(payload);
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === "AbortError") {
          return;
        }
        if (!controller.signal.aborted) {
          setReportError(error instanceof Error ? error.message : "Failed to load report");
        }
      } finally {
        if (!controller.signal.aborted) {
          setReportLoading(false);
        }
      }
    }

    void loadReport();
    return () => controller.abort();
  }, [comparisonStatus, reportJsonUrl]);

  return { report, reportLoading, reportError };
}

async function waitForOperationCompletion(
  convex: ReturnType<typeof useConvex>,
  operationId: Id<"operations">
) {
  const startedAt = Date.now();
  while (Date.now() - startedAt < 90_000) {
    const operation = await convex.query(api.operations.get, { operationId });
    if (operation?.status === "completed") {
      return;
    }
    if (operation?.status === "failed") {
      throw new Error(operation.error ?? "Failed to generate annotated Takeoff PDF");
    }
    await new Promise((resolve) => setTimeout(resolve, 1_000));
  }
  throw new Error("Timed out while generating annotated Takeoff PDF");
}

function RevisionTablePreviewList({
  title,
  subtitle,
  tables,
}: {
  title: string;
  subtitle: string;
  tables: ExtractedTableRef[];
}) {
  const [loadedRows, setLoadedRows] = useState<Record<string, { headers: string[]; rows: string[][] }>>(
    {}
  );
  const [loadingTableId, setLoadingTableId] = useState<string | null>(null);
  const [errorsByTableId, setErrorsByTableId] = useState<Record<string, string>>({});

  const handleLoadPreview = useCallback(async (table: ExtractedTableRef) => {
    const tableId = String(table._id);
    setLoadingTableId(tableId);
    setErrorsByTableId((previous) => {
      const next = { ...previous };
      delete next[tableId];
      return next;
    });

    try {
      const payload = await getTableRowsPayload({
        tableId,
        headers: table.headers,
        rowCount: table.rowCount,
        headerCount: table.headers.length,
        rowsVersion: table.rowsVersion,
        rowsFormat: table.rowsFormat,
        rowsDownloadUrl: table.rowsDownloadUrl,
      });
      setLoadedRows((previous) => ({ ...previous, [tableId]: payload }));
    } catch (error) {
      console.error(error);
      setErrorsByTableId((previous) => ({
        ...previous,
        [tableId]: error instanceof Error ? error.message : "Failed to load table preview",
      }));
    } finally {
      setLoadingTableId((current) => (current === tableId ? null : current));
    }
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{subtitle}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {tables.length === 0 ? (
          <p className="text-sm text-muted-foreground">No extracted tables yet.</p>
        ) : (
          tables.map((table) => {
            const tableId = String(table._id);
            const payload = loadedRows[tableId];
            const tableError = errorsByTableId[tableId];
            const isLoading = loadingTableId === tableId;

            return (
              <div key={tableId} className="rounded-md border p-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="text-sm font-semibold">{table.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Page {table.pageNumber} · {table.rowCount} rows · {table.headers.length} columns
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isLoading}
                    onClick={() => void handleLoadPreview(table)}
                  >
                    {isLoading ? "Loading..." : payload ? "Reload Preview" : "Load Preview"}
                  </Button>
                </div>

                {tableError && (
                  <p className="mt-2 text-xs text-destructive">{tableError}</p>
                )}

                {payload && (
                  <TablePreview
                    headers={payload.headers}
                    rows={payload.rows}
                    className="mt-3"
                  />
                )}
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

interface CompletedScheduleReportSectionProps {
  report: ScheduleComparisonReport | null;
  reportLoading: boolean;
  reportError: string | null;
  stats?: {
    matchedTables: number;
    addedTables: number;
    removedTables: number;
    changedRows: number;
    addedRows: number;
    removedRows: number;
    modifiedRows: number;
  };
  comparisonName?: string;
  reportPdfUrl?: string | null;
  reportWorkbookUrl?: string | null;
  onDownload?: (url: string, filename: string) => Promise<void>;
  onCopyText?: (text: string, label: string) => void;
}

function CompletedScheduleReportSection({
  report,
  reportLoading,
  reportError,
  stats,
  onCopyText,
}: CompletedScheduleReportSectionProps) {
  const handleCopyText = useCallback(async (text: string, label: string) => {
    if (onCopyText) {
      onCopyText(text, label);
      return;
    }
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("Clipboard is unavailable");
      }
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch (error) {
      console.error(error);
      notifyError(error, `Failed to copy ${label}`);
    }
  }, [onCopyText]);

  if (reportLoading) {
    return (
      <div className="py-8 text-center">
        <Loader2 className="mx-auto size-6 animate-spin text-muted-foreground" />
        <p className="mt-2 text-sm text-muted-foreground">Loading comparison report...</p>
      </div>
    );
  }

  if (reportError) {
    return (
      <div className="py-8 text-center text-destructive">
        <AlertCircle className="mx-auto size-6" />
        <p className="mt-2 text-sm">{reportError}</p>
      </div>
    );
  }

  if (!report || !isScheduleReport(report)) {
    return null;
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Comparison Report</CardTitle>
          <CardDescription>
            Overview and detailed changes detected between revisions
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">Overview</h3>
              <IconButton
                label="Copy Overview"
                icon={Copy}
                onClick={() =>
                  void handleCopyText(
                    formatNarrativeCopy("Overview", report.narrative.overview),
                    "Overview"
                  )
                }
              />
            </div>
            <p className="text-sm text-muted-foreground">{report.narrative.overview}</p>
          </div>

          {report.narrative.sections.length > 0 && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold">Key Findings</h3>
              <div className="space-y-3">
                {report.narrative.sections.map((section) => (
                  <div key={section.schedule} className="rounded-md border p-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <p className="text-sm font-medium">{section.title}</p>
                        <p className="text-xs text-muted-foreground mt-1">{section.body}</p>
                      </div>
                      <IconButton
                        label="Copy Section"
                        icon={Copy}
                        onClick={() =>
                          void handleCopyText(
                            formatNarrativeCopy(section.title, section.body),
                            section.title
                          )
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <h3 className="text-sm font-semibold">Change Log</h3>
            {report.change_log.length === 0 ? (
              <p className="text-sm text-muted-foreground">No detailed changes recorded.</p>
            ) : (
              <div className="rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Schedule</TableHead>
                      <TableHead>Sheet</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Field</TableHead>
                      <TableHead>Old Value</TableHead>
                      <TableHead>New Value</TableHead>
                      <TableHead>Source Reference</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.change_log.map((entry, index) => (
                      <TableRow key={index}>
                        <TableCell>{entry.schedule}</TableCell>
                        <TableCell>{entry.sheet_no}</TableCell>
                        <TableCell>{entry.item_identifier}</TableCell>
                        <TableCell>
                          <Badge variant="outline" size="sm">{formatChangeType(entry.change_type)}</Badge>
                        </TableCell>
                        <TableCell>{entry.field_changed}</TableCell>
                        <TableCell className="text-muted-foreground">{entry.old_value}</TableCell>
                        <TableCell>{entry.new_value}</TableCell>
                        <TableCell className="text-muted-foreground">{entry.source_reference}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {stats && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold">Statistics</h3>
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                <div className="rounded-md border p-3 text-center">
                  <p className="text-2xl font-bold">{stats.matchedTables}</p>
                  <p className="text-xs text-muted-foreground">Matched Tables</p>
                </div>
                <div className="rounded-md border p-3 text-center">
                  <p className="text-2xl font-bold">{stats.addedTables}</p>
                  <p className="text-xs text-muted-foreground">Added Tables</p>
                </div>
                <div className="rounded-md border p-3 text-center">
                  <p className="text-2xl font-bold">{stats.removedTables}</p>
                  <p className="text-xs text-muted-foreground">Removed Tables</p>
                </div>
                <div className="rounded-md border p-3 text-center">
                  <p className="text-2xl font-bold">{stats.changedRows}</p>
                  <p className="text-xs text-muted-foreground">Changed Rows</p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

interface MultiDocumentTakeoffBridgeCardProps {
  comparisonId: Id<"revPilotScheduleComparisons">;
  comparison: {
    projectId: Id<"projects">;
    v1TakeoffJobId?: Id<"takeoffPilotJobs">;
    v2TakeoffJobId?: Id<"takeoffPilotJobs">;
    reviewStatus?: string;
  };
  documentPairCount: number;
  takeoffJobs: Array<{
    _id: Id<"takeoffPilotJobs">;
    projectId: Id<"projects">;
    name: string;
    revisionNumber?: number;
  }>;
  isCreating: boolean;
  linkJobId: string;
  onLinkJobIdChange: (value: string) => void;
  onOpenRevisionDialog: () => void;
  onLinkReview: () => void;
}

function MultiDocumentTakeoffBridgeCard({
  comparisonId,
  comparison,
  documentPairCount,
  takeoffJobs,
  isCreating,
  linkJobId,
  onLinkJobIdChange,
  onOpenRevisionDialog,
  onLinkReview,
}: MultiDocumentTakeoffBridgeCardProps) {
  // Linking and creating Takeoff revisions are project writes; reviewers only open linked jobs.
  const canEdit = useCanEdit();
  const linkedSourceJobId = comparison.v1TakeoffJobId ?? null;
  const linkedCurrentJobId = comparison.v2TakeoffJobId ?? null;
  const availableTakeoffJobs = takeoffJobs.filter((job) => job.projectId === comparison.projectId);
  const linkedSourceJob = availableTakeoffJobs.find((job) => job._id === linkedSourceJobId);
  const linkedCurrentJob = availableTakeoffJobs.find((job) => job._id === linkedCurrentJobId);
  const canCreateRevision = comparison.reviewStatus === "completed" && linkedSourceJobId && !linkedCurrentJobId;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitBranchPlus className="size-5" />
          Takeoff Pilot Revision
        </CardTitle>
        <CardDescription>
          Multi-document reviews can be linked to a source Takeoff job, then used to create a full v2 Takeoff revision after review completion.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          <Badge variant="outline" size="sm" className="tabular-nums">
            {documentPairCount} {documentPairCount === 1 ? "Document Pair" : "Document Pairs"}
          </Badge>
          {linkedSourceJob ? (
            <Badge variant="secondary" size="sm">Linked to {linkedSourceJob.name}</Badge>
          ) : null}
        </div>
        {linkedCurrentJobId ? (
          <div className="grid gap-2 sm:grid-cols-2">
            {linkedSourceJobId ? (
              <Button asChild variant="outline" className="justify-center">
                <AppLink
                  to="/takeoffPilot/$jobId"
                  params={{ jobId: linkedSourceJobId }}
                  search={{ returnToRevPilotComparisonId: comparisonId }}
                >
                  Open {linkedSourceJob?.name ?? "Original"} Takeoff (v1)
                </AppLink>
              </Button>
            ) : null}
            <Button asChild variant="outline" className="justify-center">
              <AppLink
                to="/takeoffPilot/$jobId"
                params={{ jobId: linkedCurrentJobId }}
                search={{ returnToRevPilotComparisonId: comparisonId }}
              >
                Open {linkedCurrentJob?.name ?? "New"} Takeoff (v2)
              </AppLink>
            </Button>
          </div>
        ) : linkedSourceJobId ? (
          <div className="max-w-md space-y-3">
            {canEdit && canCreateRevision ? (
              <Button onClick={onOpenRevisionDialog} disabled={isCreating} className="w-full justify-center">
                {isCreating ? <Loader2 className="size-4 animate-spin" /> : <GitBranchPlus className="size-4" />}
                New Takeoff Pilot Revision
              </Button>
            ) : null}
            <Button asChild variant="outline" className="w-full justify-center">
              <AppLink
                to="/takeoffPilot/$jobId"
                params={{ jobId: linkedSourceJobId }}
                search={{ returnToRevPilotComparisonId: comparisonId }}
              >
                Open {linkedSourceJob?.name ?? "Original"} Takeoff (v1)
              </AppLink>
            </Button>
            {canEdit && !canCreateRevision ? (
              <p className="text-sm text-muted-foreground">
                Complete the RevPilot review before creating the Takeoff Pilot revision.
              </p>
            ) : null}
          </div>
        ) : !canEdit ? (
          <p className="text-sm text-muted-foreground">This review isn't linked to a Takeoff job yet.</p>
        ) : (
          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <Select value={linkJobId} onValueChange={onLinkJobIdChange} disabled={isCreating}>
              <SelectTrigger>
                <SelectValue placeholder="Select source Takeoff job" />
              </SelectTrigger>
              <SelectContent>
                {availableTakeoffJobs.map((job) => (
                  <SelectItem key={job._id} value={String(job._id)}>
                    {job.name} (R{job.revisionNumber ?? 1})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={onLinkReview} disabled={!linkJobId || isCreating}>
              Link Takeoff Job
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface TakeoffRevisionScheduleDocument {
  documentName: string;
  currentRevisionNumber?: number;
  revisions: Array<{
    _id: Id<"projectDocumentRevisions">;
    revisionName: string;
    revisionNumber: number;
    originalFilename: string;
    isCurrent: boolean;
    hasCompletedScheduleExtraction: boolean;
  }>;
}

interface RevPilotTakeoffRevisionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sourceName: string;
  targetDescription: string;
  revisionName: string;
  onRevisionNameChange: (value: string) => void;
  useAnnotatedPdf: boolean;
  onUseAnnotatedPdfChange: (value: boolean) => void;
  annotatedReportAvailable: boolean;
  scheduleDocument?: TakeoffRevisionScheduleDocument | null;
  scheduleRevisionId: string;
  onScheduleRevisionIdChange: (value: string) => void;
  scheduleReady: boolean;
  isCreating: boolean;
  canSubmit: boolean;
  onCreate: () => void;
}

function getRevisionLabel(revision: {
  revisionName: string;
  revisionNumber: number;
  originalFilename: string;
}) {
  return `${revision.revisionName} (r${revision.revisionNumber}) - ${revision.originalFilename}`;
}

function RevPilotTakeoffRevisionDialog({
  open,
  onOpenChange,
  sourceName,
  targetDescription,
  revisionName,
  onRevisionNameChange,
  useAnnotatedPdf,
  onUseAnnotatedPdfChange,
  annotatedReportAvailable,
  scheduleDocument,
  scheduleRevisionId,
  onScheduleRevisionIdChange,
  scheduleReady,
  isCreating,
  canSubmit,
  onCreate,
}: RevPilotTakeoffRevisionDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-hidden sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitBranchPlus className="size-5" />
            New Takeoff Pilot Revision
          </DialogTitle>
          <DialogDescription>
            Create a Takeoff revision from {sourceName} for {targetDescription}.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 overflow-y-auto pe-1">
          <div className="space-y-2">
            <label className="text-sm font-semibold" htmlFor="revpilot-takeoff-revision-name">
              Revision Name
            </label>
            <Input
              id="revpilot-takeoff-revision-name"
              value={revisionName}
              onChange={(event) => onRevisionNameChange(event.target.value)}
              placeholder={`${sourceName} RevPilot revision`}
              disabled={isCreating}
            />
          </div>

          <div className="space-y-2">
            <p id="revpilot-takeoff-base-pdf-label" className="text-sm font-semibold">
              Base Plan PDF
            </p>
            <div
              role="radiogroup"
              aria-labelledby="revpilot-takeoff-base-pdf-label"
              className="grid gap-3 sm:grid-cols-2"
            >
              <button
                type="button"
                role="radio"
                aria-checked={!useAnnotatedPdf}
                className={cn(
                  "rounded-lg border-2 p-3 text-left transition-colors",
                  !useAnnotatedPdf ? "border-primary bg-primary/5" : "bg-card hover:bg-muted/50"
                )}
                disabled={isCreating}
                onClick={() => onUseAnnotatedPdfChange(false)}
              >
                <span className="block text-sm font-semibold">New revision PDF</span>
                <span className="mt-1 block text-xs text-muted-foreground">
                  Use the selected project document revision as the Takeoff base.
                </span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={useAnnotatedPdf}
                className={cn(
                  "rounded-lg border-2 p-3 text-left transition-colors",
                  useAnnotatedPdf ? "border-primary bg-primary/5" : "bg-card hover:bg-muted/50",
                  !annotatedReportAvailable && "cursor-not-allowed opacity-60"
                )}
                disabled={isCreating || !annotatedReportAvailable}
                onClick={() => onUseAnnotatedPdfChange(true)}
              >
                <span className="block text-sm font-semibold">Annotated RevPilot PDF</span>
                <span className="mt-1 block text-xs text-muted-foreground">
                  Use included RevPilot annotations as the target Takeoff base.
                </span>
              </button>
            </div>
          </div>

          {scheduleDocument ? (
            <div className="space-y-2 rounded-lg border bg-muted/20 p-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold">Schedule Revision</p>
                  <p className="text-xs text-muted-foreground">{scheduleDocument.documentName}</p>
                </div>
                {scheduleDocument.currentRevisionNumber !== undefined ? (
                  <RevisionBadge number={scheduleDocument.currentRevisionNumber} current />
                ) : null}
              </div>
              <Select
                value={scheduleRevisionId}
                onValueChange={onScheduleRevisionIdChange}
                disabled={isCreating}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select schedule revision" />
                </SelectTrigger>
                <SelectContent>
                  {scheduleDocument.revisions.map((revision) => (
                    <SelectItem key={revision._id} value={String(revision._id)}>
                      {getRevisionLabel(revision)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!scheduleReady ? (
                <p className="text-xs text-destructive">
                  This schedule revision is not ready yet. Wait for schedule extraction to complete.
                </p>
              ) : null}
            </div>
          ) : (
            <div className="space-y-1 rounded-lg border bg-muted/20 p-3">
              <p className="text-sm font-semibold">No Schedule Attached</p>
              <p className="text-xs text-muted-foreground">
                This revision will copy custom items, columns, and measurements onto the selected plan revisions.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" disabled={isCreating} onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!canSubmit} onClick={onCreate}>
            {isCreating ? <Loader2 className="size-4 animate-spin" /> : <GitBranchPlus className="size-4" />}
            Create Revision
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// RevPilot bridge state spans linked jobs, revision creation, and export base options in one card.
// eslint-disable-next-line complexity
function RevPilotTakeoffBridge({ comparisonId }: { comparisonId: Id<"revPilotScheduleComparisons"> }) {
  const canEdit = useCanEdit();
  const convex = useConvex();
  const { data: details } = useQuery(convexQuery(api.scheduleComparisons.getWithDetails, { comparisonId }));
  const { data: options } = useQuery(
    convexQuery(api.revPilotTakeoffBridge.getTakeoffBridgeOptions, { comparisonId })
  );
  const { data: takeoffJobs } = useQuery(convexQuery(api.takeoffPilot.jobs.listAll, {}));
  const createRevision = useMutation(api.takeoffPilot.jobRevisions.createRevisionFromJob);
  const createTakeoffRevisionFromReview = useMutation(api.revPilotTakeoffBridge.createTakeoffRevisionFromReview);
  const linkReviewToTakeoffJob = useMutation(api.revPilotTakeoffBridge.linkReviewToTakeoffJob);
  const useAnnotatedPdfForTakeoffRevision = useMutation(
    api.revPilotTakeoffBridge.useAnnotatedPdfForTakeoffRevision
  );
  const linkTakeoffJobs = useMutation(api.revPilotTakeoffBridge.linkTakeoffJobs);
  const navigate = useAppNavigate();
  const [selectedSourceJobId, setSelectedSourceJobId] = useState("");
  const [linkJobId, setLinkJobId] = useState("");
  const [revisionName, setRevisionName] = useState("");
  const [useAnnotatedPdf, setUseAnnotatedPdf] = useState(false);
  const [scheduleRevisionId, setScheduleRevisionId] = useState("");
  const [isRevisionDialogOpen, setIsRevisionDialogOpen] = useState(false);
  useOnEditAccessLost(() => setIsRevisionDialogOpen(false));
  const [isCreating, setIsCreating] = useState(false);
  const multiDocumentPairs = details?.documentPairs ?? [];

  const handleLinkReview = async () => {
    if (!linkJobId) return;
    setIsCreating(true);
    try {
      await linkReviewToTakeoffJob({
        comparisonId,
        v1TakeoffJobId: linkJobId as Id<"takeoffPilotJobs">,
      });
      toast.success("Takeoff job linked");
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to link Takeoff job");
      console.error(error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCreateReviewRevision = async () => {
    setIsCreating(true);
    try {
      const createdJobId = await createTakeoffRevisionFromReview({
        comparisonId,
        ...(revisionName.trim() ? { name: revisionName.trim() } : {}),
        ...(sourceRevisionOptions?.scheduleDocument && scheduleRevisionId
          ? { scheduleRevisionId: scheduleRevisionId as Id<"projectDocumentRevisions"> }
          : {}),
        useAnnotatedPdf,
      });
      toast.success("Takeoff revision started");
      navigate({
        to: "/takeoffPilot/$jobId",
        params: { jobId: createdJobId },
        search: { returnToRevPilotComparisonId: comparisonId },
      });
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to create Takeoff revision");
      console.error(error);
    } finally {
      setIsCreating(false);
    }
  };

  const sourceOptions = options?.sourceJobs ?? [];
  const selectedOption = sourceOptions.find((option) => String(option.job._id) === selectedSourceJobId);
  const reviewSourceJobId =
    multiDocumentPairs.length > 0 && details?.comparison?.v1TakeoffJobId
      ? details.comparison.v1TakeoffJobId
      : selectedOption?.job._id;
  const { data: sourceRevisionOptions } = useQuery(
    convexQuery(
      api.takeoffPilot.jobRevisions.getRevisionOptionsForJob,
      reviewSourceJobId ? { jobId: reviewSourceJobId } : "skip"
    )
  );

  useEffect(() => {
    if (!options || selectedSourceJobId || sourceOptions.length === 0) {
      return;
    }
    const firstOption = sourceOptions[0]!;
    setSelectedSourceJobId(String(firstOption.job._id));
    setRevisionName(`${firstOption.job.name} R${firstOption.job.revisionNumber + 1}`);
    setScheduleRevisionId("");
  }, [options, selectedSourceJobId, sourceOptions]);

  useEffect(() => {
    if (!sourceRevisionOptions) {
      return;
    }
    setScheduleRevisionId((current) =>
      current || (sourceRevisionOptions.scheduleDocument ? String(sourceRevisionOptions.scheduleDocument.currentRevisionId) : "")
    );
  }, [sourceRevisionOptions]);

  const linkedCurrentJobId =
    typeof options?.linkedTakeoffJobIds.v2TakeoffJobId === "string"
      ? options.linkedTakeoffJobIds.v2TakeoffJobId
      : null;
  const linkedSourceJobId =
    typeof options?.linkedTakeoffJobIds.v1TakeoffJobId === "string"
      ? options.linkedTakeoffJobIds.v1TakeoffJobId
      : null;
  const linkedSourceTakeoffName = options?.linkedTakeoffJobs.v1TakeoffJob?.name;
  const linkedCurrentTakeoffName = options?.linkedTakeoffJobs.v2TakeoffJob?.name;
  const selectedScheduleRevision = sourceRevisionOptions?.scheduleDocument?.revisions.find(
    (revision) => String(revision._id) === scheduleRevisionId
  );
  const scheduleReady = sourceRevisionOptions?.scheduleDocument
    ? selectedScheduleRevision !== undefined &&
      (selectedScheduleRevision.isCurrent || selectedScheduleRevision.hasCompletedScheduleExtraction)
    : true;
  const annotatedReportAvailable = Boolean(
    options?.annotatedReportAvailable ||
      details?.comparison?.reportPdfFileKey
  );
  const canCreateTakeoffRevision = Boolean(
    reviewSourceJobId &&
      sourceRevisionOptions &&
      revisionName.trim() &&
      (!useAnnotatedPdf || annotatedReportAvailable) &&
      (!sourceRevisionOptions?.scheduleDocument || scheduleRevisionId) &&
      scheduleReady &&
      !isCreating
  );

  const handleCreateRevision = async () => {
    if (!selectedOption) {
      return;
    }
    setIsCreating(true);
    try {
      const createdJobId = await createRevision({
        sourceJobId: selectedOption.job._id,
        name: revisionName.trim() || `${selectedOption.job.name} R${selectedOption.job.revisionNumber + 1}`,
        ...(sourceRevisionOptions?.scheduleDocument && scheduleRevisionId
          ? { scheduleRevisionId: scheduleRevisionId as Id<"projectDocumentRevisions"> }
          : {}),
        planRevisionSelections: [
          {
            sourcePlanDocumentId: selectedOption.sourcePlanDocument._id,
            targetRevisionId: selectedOption.targetRevision._id,
          },
        ],
      });
      if (useAnnotatedPdf && options?.annotatedReportAvailable) {
        const operationId = await useAnnotatedPdfForTakeoffRevision({
          comparisonId,
          sourceTakeoffPlanDocumentId: selectedOption.sourcePlanDocument._id,
          takeoffJobId: createdJobId,
        });
        toast.info("Generating included-change annotated PDF for Takeoff...");
        await waitForOperationCompletion(convex, operationId);
      }
      await linkTakeoffJobs({
        comparisonId,
        v1TakeoffJobId: selectedOption.job._id,
        v2TakeoffJobId: createdJobId,
      });
      toast.success("Takeoff revision started");
      navigate({
        to: "/takeoffPilot/$jobId",
        params: { jobId: createdJobId },
        search: { returnToRevPilotComparisonId: comparisonId },
      });
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to create Takeoff revision");
      console.error(error);
    } finally {
      setIsCreating(false);
    }
  };

  const isMultiDocumentReview = multiDocumentPairs.length > 0;
  const revisionDialog = (
    <RevPilotTakeoffRevisionDialog
      open={isRevisionDialogOpen}
      onOpenChange={setIsRevisionDialogOpen}
      sourceName={sourceRevisionOptions?.sourceJob.name ?? selectedOption?.job.name ?? linkedSourceTakeoffName ?? "Takeoff"}
      targetDescription={
        selectedOption
          ? selectedOption.targetRevision.revisionName
          : isMultiDocumentReview
            ? "the reviewed plan revisions"
            : options?.v2Revision?.revisionName ?? "the current revision"
      }
      revisionName={revisionName}
      onRevisionNameChange={setRevisionName}
      useAnnotatedPdf={useAnnotatedPdf}
      onUseAnnotatedPdfChange={setUseAnnotatedPdf}
      annotatedReportAvailable={annotatedReportAvailable}
      scheduleDocument={sourceRevisionOptions?.scheduleDocument ?? null}
      scheduleRevisionId={scheduleRevisionId}
      onScheduleRevisionIdChange={setScheduleRevisionId}
      scheduleReady={scheduleReady}
      isCreating={isCreating}
      canSubmit={canCreateTakeoffRevision}
      onCreate={() => void (isMultiDocumentReview ? handleCreateReviewRevision() : handleCreateRevision())}
    />
  );

  if (multiDocumentPairs.length > 0 && details?.comparison) {
    return (
      <>
        <MultiDocumentTakeoffBridgeCard
          comparisonId={comparisonId}
          comparison={details.comparison}
          documentPairCount={multiDocumentPairs.length}
          takeoffJobs={takeoffJobs ?? []}
          isCreating={isCreating}
          linkJobId={linkJobId}
          onLinkJobIdChange={setLinkJobId}
          onOpenRevisionDialog={() => setIsRevisionDialogOpen(true)}
          onLinkReview={() => void handleLinkReview()}
        />
        {revisionDialog}
      </>
    );
  }

  if (!options || !options.v1Revision || !options.v2Revision) {
    return null;
  }

  if (linkedCurrentJobId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <GitBranchPlus className="size-5" />
            Takeoff Pilot Revision
          </CardTitle>
          <CardDescription>
            This RevPilot comparison is linked to a Takeoff Pilot revision for quantity deltas.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {linkedSourceJobId ? (
            <Button asChild variant="outline">
              <AppLink
                to="/takeoffPilot/$jobId"
                params={{ jobId: linkedSourceJobId }}
                search={{ returnToRevPilotComparisonId: comparisonId }}
              >
                Open {linkedSourceTakeoffName ?? "Original"} Takeoff (v1)
              </AppLink>
            </Button>
          ) : null}
          <Button asChild variant="outline">
            <AppLink
              to="/takeoffPilot/$jobId"
              params={{ jobId: linkedCurrentJobId }}
              search={{ returnToRevPilotComparisonId: comparisonId }}
            >
              Open {linkedCurrentTakeoffName ?? "New"} Takeoff (v2)
            </AppLink>
          </Button>
        </CardContent>
      </Card>
    );
  }

  // The remaining card only creates a Takeoff revision, which reviewers can't do.
  if (!canEdit) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitBranchPlus className="size-5" />
          Takeoff Pilot Revision
        </CardTitle>
        <CardDescription>
          Bring forward prior Takeoff Pilot annotations from {options.v1Revision.revisionName ?? "the previous revision"} to {options.v2Revision.revisionName ?? "the current revision"}.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {sourceOptions.length === 0 ? (
          <Alert>
            <AlertTitle>No prior Takeoff Pilot found</AlertTitle>
            <AlertDescription>
              Quantity deltas require a completed Takeoff Pilot that uses the previous plan revision.
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                value={selectedSourceJobId}
                onValueChange={(value) => {
                  setSelectedSourceJobId(value);
                  setScheduleRevisionId("");
                  const next = sourceOptions.find((option) => String(option.job._id) === value);
                  if (next) {
                    setRevisionName(`${next.job.name} R${next.job.revisionNumber + 1}`);
                  }
                }}
                disabled={isCreating}
              >
                <SelectTrigger aria-label="Select prior Takeoff Pilot job">
                  <SelectValue placeholder="Select prior Takeoff Pilot" />
                </SelectTrigger>
                <SelectContent>
                  {sourceOptions.map((option) => (
                    <SelectItem key={option.job._id} value={String(option.job._id)}>
                      {option.job.name} · {option.sourcePlanDocument.displayName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                aria-label="New Takeoff revision name"
                value={revisionName}
                onChange={(event) => setRevisionName(event.target.value)}
                placeholder="New Takeoff revision name"
                disabled={isCreating}
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setIsRevisionDialogOpen(true)} disabled={!selectedOption || isCreating}>
                {isCreating ? <Loader2 className="size-4 animate-spin" /> : <GitBranchPlus className="size-4" />}
                New Takeoff Pilot Revision
              </Button>
              {selectedOption ? (
                <Button asChild variant="outline">
                  <AppLink
                    to="/takeoffPilot/$jobId"
                    params={{ jobId: selectedOption.job._id }}
                    search={{ returnToRevPilotComparisonId: comparisonId }}
                  >
                    Open {selectedOption.job.name} in TakeoffPilot
                  </AppLink>
                </Button>
              ) : null}
            </div>
            {selectedOption ? (
              <p className="text-xs text-muted-foreground">
                This will copy annotations/counts from {selectedOption.sourcePlanDocument.revisionName} and attach them to {selectedOption.targetRevision.revisionName}.
              </p>
            ) : null}
          </>
        )}
      </CardContent>
      {revisionDialog}
    </Card>
  );
}

function QuantityDeltaSection({ comparisonId }: { comparisonId: Id<"revPilotScheduleComparisons"> }) {
  const { data: deltaPayload } = useQuery(
    convexQuery(api.takeoffPilot.quantityDeltas.listForComparison, { comparisonId })
  );
  const [visibleColumns, setVisibleColumns] = useState<QuantityDeltaOptionalColumn[]>([
    ...QUANTITY_DELTA_OPTIONAL_COLUMNS,
  ]);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const measurementDisplays = DELTA_MEASUREMENT_OPTIONS.filter((measurement) => visibleColumns.includes(measurement));
  const showDrawingColumn = visibleColumns.includes("drawing");

  useEffect(() => {
    const hiddenColumnIds = new Set<string>();

    if (!showDrawingColumn) {
      hiddenColumnIds.add("drawing");
    }

    for (const measurement of DELTA_MEASUREMENT_OPTIONS) {
      if (visibleColumns.includes(measurement)) {
        continue;
      }

      const measurementColumnIds = getDeltaMeasurementColumnIds(measurement);
      hiddenColumnIds.add(measurementColumnIds.previous);
      hiddenColumnIds.add(measurementColumnIds.current);
      hiddenColumnIds.add(measurementColumnIds.delta);
    }

    if (hiddenColumnIds.size === 0) {
      return;
    }

    setColumnFilters((current) => current.filter((filter) => !hiddenColumnIds.has(filter.id)));
    setSorting((current) => current.filter((sort) => !hiddenColumnIds.has(sort.id)));
  }, [showDrawingColumn, visibleColumns]);

  if (!deltaPayload) {
    return null;
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Quantity Deltas</CardTitle>
            <CardDescription>Before/current quantities grouped by drawing and tag.</CardDescription>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {deltaPayload.rows.length > 0 && !deltaPayload.unavailableReason ? (
              <>
                <QuantityDeltaColumnSelect
                  selectedColumns={visibleColumns}
                  onSelectedColumnsChange={setVisibleColumns}
                />
                <IconButton
                  label="Open quantity deltas fullscreen"
                  icon={Maximize2}
                  variant="outline"
                  onClick={() => setIsExpanded(true)}
                />
              </>
            ) : null}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {deltaPayload.unavailableReason ? (
          <p className="text-sm text-muted-foreground">{deltaPayload.unavailableReason}</p>
        ) : deltaPayload.rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">No quantity deltas found.</p>
        ) : (
          <QuantityDeltaTable
            rows={deltaPayload.rows}
            measurementDisplays={measurementDisplays}
            sorting={sorting}
            onSortingChange={setSorting}
            columnFilters={columnFilters}
            onColumnFiltersChange={setColumnFilters}
            showDrawingColumn={showDrawingColumn}
          />
        )}
      </CardContent>
      <Dialog open={isExpanded} onOpenChange={setIsExpanded}>
        <DialogContent className="flex h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)] flex-col sm:max-w-[calc(100vw-2rem)]">
          <DialogHeader className="gap-3 pe-12 sm:flex-row sm:items-start sm:justify-between">
            <DialogTitle>Quantity Deltas</DialogTitle>
            <QuantityDeltaColumnSelect
              selectedColumns={visibleColumns}
              onSelectedColumnsChange={setVisibleColumns}
            />
          </DialogHeader>
          <div className="min-h-0 flex-1">
            <QuantityDeltaTable
              rows={deltaPayload.rows}
              measurementDisplays={measurementDisplays}
              sorting={sorting}
              onSortingChange={setSorting}
              columnFilters={columnFilters}
              onColumnFiltersChange={setColumnFilters}
              showDrawingColumn={showDrawingColumn}
              expanded
            />
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export function QuantityDeltaColumnSelect({
  selectedColumns,
  onSelectedColumnsChange,
}: QuantityDeltaColumnSelectProps) {
  const columnSections = useMemo<MultiSelectSection[]>(
    () => [
      {
        id: "quantity-delta-columns",
        label: "Columns",
        options: [
          { value: "drawing", label: "Drawing" },
          ...DELTA_MEASUREMENT_OPTIONS.map((measurement) => ({ value: measurement, label: measurement })),
        ],
      },
    ],
    []
  );
  const handleSelectColumn = (column: string) => {
    const typedColumn = column as QuantityDeltaOptionalColumn;
    if (!QUANTITY_DELTA_OPTIONAL_COLUMNS.includes(typedColumn) || selectedColumns.includes(typedColumn)) {
      return;
    }

    onSelectedColumnsChange(
      QUANTITY_DELTA_OPTIONAL_COLUMNS.filter((option) => [...selectedColumns, typedColumn].includes(option))
    );
  };
  const handleRemoveColumn = (column: string) => {
    onSelectedColumnsChange(selectedColumns.filter((selected) => selected !== column));
  };

  return (
    <MultiSelect
      sections={columnSections}
      selected={selectedColumns}
      onSelect={handleSelectColumn}
      onRemove={handleRemoveColumn}
      onSelectAll={() => onSelectedColumnsChange([...QUANTITY_DELTA_OPTIONAL_COLUMNS])}
      onDeselectAll={() => onSelectedColumnsChange([])}
      placeholder="Columns"
      triggerAriaLabel="Quantity delta columns"
      triggerWidth="w-[18ch]"
      dropdownWidth="w-64"
      columns={1}
      triggerLabel="Columns"
      triggerIcon={<Columns2 className="size-3.5" />}
      showSelectedBadges={false}
    />
  );
}

export function QuantityDeltaTable({
  rows,
  measurementDisplays,
  expanded = false,
  sorting: controlledSorting,
  onSortingChange,
  columnFilters: controlledColumnFilters,
  onColumnFiltersChange,
  showDrawingColumn: controlledShowDrawingColumn,
}: QuantityDeltaTableProps) {
  const [localSorting, setLocalSorting] = useState<SortingState>([]);
  const [localColumnFilters, setLocalColumnFilters] = useState<ColumnFiltersState>([]);
  const sorting = controlledSorting ?? localSorting;
  const columnFilters = controlledColumnFilters ?? localColumnFilters;
  const setSorting = onSortingChange ?? setLocalSorting;
  const setColumnFilters = onColumnFiltersChange ?? setLocalColumnFilters;
  const showDrawingColumn = controlledShowDrawingColumn ?? true;

  const textColumnWidth = measurementDisplays.length === 0 ? 240 : 220;
  const itemColumnWidth = measurementDisplays.length === 0
    ? (showDrawingColumn ? 220 : 320)
    : (showDrawingColumn ? 160 : 240);
  const quantityColumnWidth = 82;
  const measurementColumnWidth = 116;
  const minWidth =
    (showDrawingColumn ? textColumnWidth : 0) +
    itemColumnWidth +
    quantityColumnWidth * 3 +
    measurementDisplays.length * measurementColumnWidth * 3;
  const columns = useMemo<ColumnDef<typeof appTableFeatures, QuantityDeltaRow>[]>(
    () => [
      {
        accessorKey: "drawing",
        sortFn: "alphanumeric",
        filterFn: quantityDeltaMultiSelectFilter,
      },
      {
        accessorKey: "itemType",
        sortFn: "alphanumeric",
        filterFn: quantityDeltaMultiSelectFilter,
      },
      { accessorKey: "previousQuantity", sortFn: "basic" },
      { accessorKey: "currentQuantity", sortFn: "basic" },
      { accessorKey: "delta", sortFn: "basic" },
      { accessorKey: "previousLf", sortFn: "basic" },
      { accessorKey: "currentLf", sortFn: "basic" },
      { accessorKey: "lfDelta", sortFn: "basic" },
      { accessorKey: "previousSqft", sortFn: "basic" },
      { accessorKey: "currentSqft", sortFn: "basic" },
      { accessorKey: "sqftDelta", sortFn: "basic" },
      { accessorKey: "previousCf", sortFn: "basic" },
      { accessorKey: "currentCf", sortFn: "basic" },
      { accessorKey: "cfDelta", sortFn: "basic" },
    ],
    []
  );
  const table = useTable({
    features: appTableFeatures,
    data: rows,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
  });
  const visibleRows = table.getRowModel().rows;
  const drawingColumn = table.getColumn("drawing");
  const itemTypeColumn = table.getColumn("itemType");
  const selectedDrawingFilters = (drawingColumn?.getFilterValue() as string[] | undefined) ?? [];
  const selectedItemTypeFilters = (itemTypeColumn?.getFilterValue() as string[] | undefined) ?? [];
  const drawingFilterOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => row.drawing))).sort((left, right) => left.localeCompare(right)),
    [rows]
  );
  const tagFilterOptions = useMemo(
    () => Array.from(new Set(rows.map((row) => row.itemType))).sort((left, right) => left.localeCompare(right)),
    [rows]
  );
  const tagFilterSections = useMemo<MultiSelectSection[]>(
    () => [
      {
        id: "quantity-delta-tags",
        label: "Tags",
        options: tagFilterOptions.map((tag) => ({ value: tag, label: tag || "Blank" })),
      },
    ],
    [tagFilterOptions]
  );
  const setFilterValues = (columnId: "drawing" | "itemType", values: string[]) => {
    table.getColumn(columnId)?.setFilterValue(values.length > 0 ? values : undefined);
  };
  const handleDrawingFilterChange = (drawing: string, checked: boolean) => {
    const nextFilters = new Set(selectedDrawingFilters);
    if (checked) {
      nextFilters.add(drawing);
    } else {
      nextFilters.delete(drawing);
    }
    setFilterValues("drawing", Array.from(nextFilters));
  };
  const handleSelectTagFilter = (tag: string) => {
    if (selectedItemTypeFilters.includes(tag)) {
      return;
    }

    setFilterValues("itemType", [...selectedItemTypeFilters, tag]);
  };
  const handleRemoveTagFilter = (tag: string) => {
    setFilterValues("itemType", selectedItemTypeFilters.filter((selected) => selected !== tag));
  };
  const visibleColumnCount = (showDrawingColumn ? 1 : 0) + 4 + measurementDisplays.length * 3;
  const renderSortableHeader = (columnId: string, label: string, align: "left" | "right" = "left") => {
    const column = table.getColumn(columnId);
    const nextSortOrder = column?.getNextSortingOrder();
    const nextSortLabel =
      nextSortOrder === "asc" ? "ascending" : nextSortOrder === "desc" ? "descending" : "clear sorting";
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={cn(
          "px-2 text-xs font-bold uppercase tracking-wider",
          align === "left" ? "-ms-2" : "ms-auto -me-2 justify-end"
        )}
        onClick={column?.getToggleSortingHandler()}
        aria-label={`Sort quantity deltas by ${label} ${nextSortLabel}`}
      >
        {label}
        {renderSortIcon(column?.getIsSorted() ?? false)}
      </Button>
    );
  };
  const renderFilterDropdown = (args: {
    label: string;
    ariaLabel: string;
    options: string[];
    selectedFilters: string[];
    onFilterChange: (value: string, checked: boolean) => void;
  }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton
          label={args.ariaLabel}
          icon={Filter}
          size="xs"
          variant={args.selectedFilters.length > 0 ? "secondary" : "ghost"}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="z-[150] max-h-80 w-64 overflow-auto">
        <DropdownMenuLabel>{args.label}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {args.options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option}
            checked={args.selectedFilters.includes(option)}
            onCheckedChange={(checked) => args.onFilterChange(option, checked === true)}
            onSelect={(event) => event.preventDefault()}
          >
            {option || "Blank"}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
  const renderFilterableHeader = (args: {
    columnId: string;
    label: string;
    filterLabel: string;
    filterAriaLabel: string;
    filterOptions: string[];
    selectedFilters: string[];
    onFilterChange: (value: string, checked: boolean) => void;
  }) => (
    <div className="flex items-center gap-2">
      {renderSortableHeader(args.columnId, args.label)}
      {renderFilterDropdown({
        label: args.filterLabel,
        ariaLabel: args.filterAriaLabel,
        options: args.filterOptions,
        selectedFilters: args.selectedFilters,
        onFilterChange: args.onFilterChange,
      })}
    </div>
  );
  const renderTagHeader = () => (
    <div className="flex items-center gap-2">
      {renderSortableHeader("itemType", "Tag")}
      <MultiSelect
        sections={tagFilterSections}
        selected={selectedItemTypeFilters}
        onSelect={handleSelectTagFilter}
        onRemove={handleRemoveTagFilter}
        onSelectAll={() => setFilterValues("itemType", tagFilterOptions)}
        onDeselectAll={() => setFilterValues("itemType", [])}
        placeholder="Tags"
        triggerAriaLabel="Filter quantity deltas by tag"
        triggerWidth="size-7 px-0"
        triggerClassName="border-0 bg-transparent shadow-none hover:bg-accent hover:text-accent-foreground"
        dropdownWidth="w-[20rem]"
        columns={1}
        triggerIcon={<Filter className="size-3.5" />}
        iconOnlyTrigger
        showSelectedBadges={false}
        wrapOptionLabels
      />
    </div>
  );

  return (
    <div className={expanded ? "flex h-full flex-col" : undefined}>
      <div
        className={expanded ? "min-h-0 flex-1 overflow-auto rounded-md" : "overflow-x-auto rounded-md"}
      >
        <Table className="table-fixed text-xs [&_td]:px-2 [&_th]:px-2" style={{ minWidth }}>
          <colgroup>
            {showDrawingColumn ? <col style={{ width: textColumnWidth }} /> : null}
            <col style={{ width: itemColumnWidth }} />
            <col style={{ width: quantityColumnWidth }} />
            <col style={{ width: quantityColumnWidth }} />
            <col style={{ width: quantityColumnWidth }} />
            {measurementDisplays.map((measurementDisplay) => (
              <Fragment key={measurementDisplay}>
                <col style={{ width: measurementColumnWidth }} />
                <col style={{ width: measurementColumnWidth }} />
                <col style={{ width: measurementColumnWidth }} />
              </Fragment>
            ))}
          </colgroup>
          <TableHeader>
            <TableRow>
              {showDrawingColumn ? (
                <TableHead>
                  {renderFilterableHeader({
                    columnId: "drawing",
                    label: "Drawing",
                    filterLabel: "Filter by drawing",
                    filterAriaLabel: "Filter quantity deltas by drawing",
                    filterOptions: drawingFilterOptions,
                    selectedFilters: selectedDrawingFilters,
                    onFilterChange: handleDrawingFilterChange,
                  })}
                </TableHead>
              ) : null}
              <TableHead>{renderTagHeader()}</TableHead>
              <TableHead className="text-right">{renderSortableHeader("previousQuantity", "Previous", "right")}</TableHead>
              <TableHead className="text-right">{renderSortableHeader("currentQuantity", "Current", "right")}</TableHead>
              <TableHead className="text-right">{renderSortableHeader("delta", "Delta", "right")}</TableHead>
              {measurementDisplays.map((measurementDisplay) => {
                const columnIds = getDeltaMeasurementColumnIds(measurementDisplay);
                return (
                  <Fragment key={measurementDisplay}>
                    <TableHead className="text-right">
                      {renderSortableHeader(columnIds.previous, `${measurementDisplay} Prev`, "right")}
                    </TableHead>
                    <TableHead className="text-right">
                      {renderSortableHeader(columnIds.current, `${measurementDisplay} Current`, "right")}
                    </TableHead>
                    <TableHead className="text-right">
                      {renderSortableHeader(columnIds.delta, `${measurementDisplay} Delta`, "right")}
                    </TableHead>
                  </Fragment>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={visibleColumnCount} className="py-10 text-center text-sm text-muted-foreground">
                  No quantity deltas match the selected filters.
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((tableRow) => {
                const row = tableRow.original;
                return (
                  <TableRow key={row.key}>
                    {showDrawingColumn ? (
                      <TableCell className="whitespace-normal break-words align-top font-medium leading-tight">{row.drawing}</TableCell>
                    ) : null}
                    <TableCell className="whitespace-normal break-words align-top leading-tight text-muted-foreground">{row.itemType}</TableCell>
                    <TableCell className="whitespace-nowrap text-right tabular-nums align-top">{row.previousQuantity}</TableCell>
                    <TableCell className="whitespace-nowrap text-right tabular-nums align-top">{row.currentQuantity}</TableCell>
                    <TableCell className="whitespace-nowrap text-right align-top">
                      <DeltaBadge value={row.delta} />
                    </TableCell>
                    {measurementDisplays.map((measurementDisplay) => {
                      const previousMeasurement = getDeltaMeasurementValue(row, measurementDisplay, "previous");
                      const currentMeasurement = getDeltaMeasurementValue(row, measurementDisplay, "current");
                      const measurementDelta = getDeltaMeasurementValue(row, measurementDisplay, "delta");

                      return (
                        <Fragment key={measurementDisplay}>
                          <TableCell className="whitespace-nowrap text-right tabular-nums align-top">
                            {formatMeasurementValue(previousMeasurement, measurementDisplay)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-right tabular-nums align-top">
                            {formatMeasurementValue(currentMeasurement, measurementDisplay)}
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-right align-top">
                            <DeltaBadge value={measurementDelta}>
                              {formatMeasurementDelta(measurementDelta, measurementDisplay)}
                            </DeltaBadge>
                          </TableCell>
                        </Fragment>
                      );
                    })}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function getDeltaMeasurementValue(
  row: QuantityDeltaRow,
  measurementDisplay: DeltaMeasurementDisplay,
  field: "previous" | "current" | "delta"
) {
  if (measurementDisplay === "LF") {
    return field === "previous" ? row.previousLf : field === "current" ? row.currentLf : row.lfDelta;
  }
  if (measurementDisplay === "SQFT") {
    return field === "previous" ? row.previousSqft : field === "current" ? row.currentSqft : row.sqftDelta;
  }
  return field === "previous" ? row.previousCf : field === "current" ? row.currentCf : row.cfDelta;
}

function getDeltaMeasurementColumnIds(measurementDisplay: DeltaMeasurementDisplay) {
  if (measurementDisplay === "LF") {
    return { previous: "previousLf", current: "currentLf", delta: "lfDelta" };
  }
  if (measurementDisplay === "SQFT") {
    return { previous: "previousSqft", current: "currentSqft", delta: "sqftDelta" };
  }
  return { previous: "previousCf", current: "currentCf", delta: "cfDelta" };
}

function NarrativeGenerationAlerts({ generationStatus }: {
  generationStatus?: {
    latestOperation: { status: string; type: string; error?: string } | null;
    isStale: boolean;
  };
}) {
  return <>
          {generationStatus?.latestOperation?.status === "failed" ? (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertTitle>{generationStatus.latestOperation.type === "revpilot_review_capture" ? "Screenshot capture failed" : "Narrative generation failed"}</AlertTitle>
              <AlertDescription>{generationStatus.latestOperation.error ?? "Try generating again. Your review notes are saved."}</AlertDescription>
            </Alert>
          ) : null}
          {generationStatus?.isStale ? (
            <Alert>
              <AlertCircle className="size-4" />
              <AlertTitle>Generated narrative is out of date</AlertTitle>
              <AlertDescription>Included areas or notes changed. Complete review to update the affected trade narratives.</AlertDescription>
            </Alert>
          ) : null}
  </>;
}

function canRequestNarrative(status: {
  includedNoteCount: number;
  latestOperation: { type: string } | null;
} | undefined, processing: boolean) {
  return Boolean(status && (status.includedNoteCount > 0 || status.latestOperation?.type === "revpilot_review_capture") && !processing);
}

export function NarrativeSummarySection({
  comparisonId,
  selectedLabel,
  onSelectedLabelChange,
  onReviewChanges,
}: {
  comparisonId: Id<"revPilotScheduleComparisons">;
  selectedLabel: string;
  onSelectedLabelChange: (label: string) => void;
  onReviewChanges?: (changeGroupId?: Id<"revPilotChangeGroups">, included?: boolean) => void;
}) {
  const { data: notes } = useQuery(
    convexQuery(api.tradeNarratives.listNarrativeSourceNotes, { comparisonId })
  );
  const { data: generationStatus } = useQuery(
    convexQuery(api.tradeNarratives.getTradeNarrativeGenerationStatus, { comparisonId })
  );
  const { data: generatedNarratives } = useQuery(
    convexQuery(api.tradeNarratives.listGeneratedTradeNarratives, { comparisonId })
  );
  const requestTradeNarrativeGeneration = useMutation(api.tradeNarratives.requestTradeNarrativeGeneration);
  const updateChangeNote = useMutation(api.revPilotChanges.updateNote);
  const [selectedNote, setSelectedNote] = useState<NarrativeSummaryNote | null>(null);
  const [isRequestingGeneration, setIsRequestingGeneration] = useState(false);
  const [isSavingSelectedNote, setIsSavingSelectedNote] = useState(false);
  const [customInstructions, setCustomInstructions] = useState("");
  // Generating narratives and editing notes are project writes; reviewers read them.
  const canEdit = useCanEdit();
  useOnEditAccessLost(() => setSelectedNote(null));
  const typedNotes = useMemo(() => (notes ?? []) as NarrativeSummaryNote[], [notes]);
  const labels = useMemo(
    () => Array.from(new Set(typedNotes.map((note) => note.label))).sort(),
    [typedNotes]
  );
  const visibleNotes = useMemo(
    () => typedNotes.filter((note) => selectedLabel === "all" || note.label === selectedLabel),
    [selectedLabel, typedNotes]
  );
  const noteById = useMemo(
    () => new Map(typedNotes.map((note) => [String(note._id), note])),
    [typedNotes]
  );
  const visibleGeneratedNarratives = useMemo(
    () =>
      (generatedNarratives ?? []).filter(
        (narrative) => selectedLabel === "all" || narrative.label.toLocaleLowerCase() === selectedLabel.toLocaleLowerCase()
      ),
    [generatedNarratives, selectedLabel]
  );
  const isGenerationProcessing = Boolean(generationStatus?.isProcessing || isRequestingGeneration);
  const canGenerateNarrative = canRequestNarrative(generationStatus, isGenerationProcessing);
  const generationButtonLabel = generatedNarratives?.length ? "Regenerate" : "Generate";

  const handleGenerateNarrative = useCallback(async () => {
    if (!canGenerateNarrative) return;
    setIsRequestingGeneration(true);
    try {
      await requestTradeNarrativeGeneration({
        comparisonId,
        customInstructions: customInstructions.trim() || undefined,
      });
      toast.success("Trade narrative generation started");
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to start trade narrative generation");
    } finally {
      setIsRequestingGeneration(false);
    }
  }, [canGenerateNarrative, comparisonId, customInstructions, requestTradeNarrativeGeneration]);

  const handleUpdateChangeNote = useCallback(
    async (noteId: Id<"revPilotChangeNotes">, body: string, label: string) => {
      setIsSavingSelectedNote(true);
      try {
        await updateChangeNote({ noteId, body, label });
        setSelectedNote(null);
        toast.success("Note updated");
      } catch (error) {
        notifyError(error, error instanceof Error ? error.message : "Failed to update note");
      } finally {
        setIsSavingSelectedNote(false);
      }
    },
    [updateChangeNote]
  );

  const groupedNotes = useMemo(() => {
    const groups = new Map<string, typeof visibleNotes>();
    for (const note of visibleNotes) {
      groups.set(note.label, [...(groups.get(note.label) ?? []), note]);
    }
    return Array.from(groups.entries());
  }, [visibleNotes]);
  const selectedGroupNotes = useMemo(
    () =>
      typedNotes
        .filter(isNarrativeGroupNote)
        .filter((note) => selectedNote?.changeGroupId && note.changeGroupId === selectedNote.changeGroupId),
    [selectedNote, typedNotes]
  );

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <CardTitle>Trade Narrative</CardTitle>
              <CardDescription>Generated trade narrative with raw source notes grouped by trade or label.</CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <RequiresCapability mode="disable">
                <Button
                  variant={generatedNarratives?.length ? "outline" : "default"}
                  size="sm"
                  disabled={!canGenerateNarrative}
                  onClick={handleGenerateNarrative}
                >
                  {isGenerationProcessing ? <Loader2 className="size-4 animate-spin" /> : null}
                  {generationStatus?.isCapturing ? "Preparing screenshots..." : isGenerationProcessing ? "Generating..." : generationButtonLabel}
                </Button>
              </RequiresCapability>
              {onReviewChanges ? (
                <Button variant="outline" size="sm" onClick={() => onReviewChanges()}>
                  Review All Changes
                </Button>
              ) : null}
              <Select value={selectedLabel} onValueChange={onSelectedLabelChange}>
                <SelectTrigger className="w-56">
                  <SelectValue placeholder="Filter by label" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All labels</SelectItem>
                  {labels.map((label) => (
                    <SelectItem key={label} value={label}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          {canEdit ? (
          <div className="rounded-md border bg-muted/20 p-3">
            <Label htmlFor="trade-narrative-custom-instructions">Custom instructions</Label>
            <p className="mt-1 text-xs text-muted-foreground">
              Optional internal testing prompt guidance. These instructions are injected into the next Generate / Regenerate run.
            </p>
            <Textarea
              id="trade-narrative-custom-instructions"
              value={customInstructions}
              onChange={(event) => setCustomInstructions(event.target.value)}
              disabled={isGenerationProcessing}
              placeholder="Example: Focus on owner-facing impacts, keep bullets short, and call out coordination risks."
              className="mt-3 min-h-24"
            />
          </div>
          ) : null}
          <NarrativeGenerationAlerts generationStatus={generationStatus} />
          {generationStatus?.executiveSummary || visibleGeneratedNarratives.length > 0 || isGenerationProcessing ? (
            <div className="space-y-4 rounded-md border bg-muted/20 p-3">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold">Generated Narrative</h4>
                <Badge variant="secondary" size="sm">Generated</Badge>
              </div>
              {isGenerationProcessing ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="size-4 animate-spin" />
                  {generationStatus?.isCapturing ? "Preparing screenshots in the background. You can leave this page." : "Generating narratives from included notes and screenshots..."}
                </div>
              ) : null}
              {generationStatus?.executiveSummary ? (
                <div>
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Executive Summary</p>
                  <p className="mt-1 whitespace-pre-wrap text-sm">{generationStatus.executiveSummary}</p>
                </div>
              ) : null}
              {visibleGeneratedNarratives.map((narrative) => (
                <div key={narrative._id} className="rounded-md border bg-background p-3">
                  <div className="flex items-center justify-between gap-2">
                    <h5 className="text-sm font-semibold">{narrative.label}</h5>
                    <Badge variant="outline" size="sm" className="tabular-nums">
                      {narrative.sourceNoteIds.length}{" "}
                      {narrative.sourceNoteIds.length === 1 ? "Source Note" : "Source Notes"}
                    </Badge>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm">{narrative.narrative}</p>
                  {narrative.sourceNoteIds.length > 0 ? (
                    <div className="mt-3 border-t pt-3">
                      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Source notes</p>
                      <div className="mt-2 space-y-2">
                        {narrative.sourceNoteIds.map((noteId) => {
                          const note = noteById.get(String(noteId));
                          return note ? (
                            <button
                              key={String(noteId)}
                              type="button"
                              className="block w-full rounded-md bg-muted/40 p-2 text-left text-xs hover:bg-muted disabled:cursor-default disabled:hover:bg-muted/40"
                              onClick={() => {
                                if (note.noteKind === "group") {
                                  setSelectedNote(note);
                                  return;
                                }
                                setSelectedNote(note);
                              }}
                            >
                              <span className="font-medium">{getNarrativeNoteSourceLabel(note)}</span>
                              <span className="mt-1 line-clamp-2 block text-muted-foreground">{note.body}</span>
                            </button>
                          ) : null;
                        })}
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}

          <div>
            <div className="mb-3 flex items-center gap-2">
              <h4 className="text-sm font-semibold">Raw User Notes</h4>
              <Badge variant="outline" size="sm">Source</Badge>
            </div>
          {notes === undefined ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" />
              Loading narrative...
            </div>
          ) : groupedNotes.length === 0 ? (
            <p className="text-sm text-muted-foreground">No RevPilot notes have been added yet.</p>
          ) : (
            <div className="space-y-4">
              {groupedNotes.map(([label, labelNotes]) => (
                <div key={label} className="rounded-md border p-3">
                  <h4 className="text-sm font-semibold">{label}</h4>
                  <div className="mt-3 space-y-3">
                    {labelNotes.map((note) => (
                      <div key={note._id} className="rounded-md bg-muted/40 p-3">
                        <div className="flex items-start justify-between gap-2">
                          <p className="text-xs text-muted-foreground">
                            {getNarrativeNoteSourceLabel(note)}
                          </p>
                          {canEdit && (note.noteKind === "group" || note.noteKind === "change") ? (
                            <Button
                              variant="ghost"
                              size="compact"
                              onClick={() => setSelectedNote(note)}
                            >
                              <Edit className="size-3" />
                              Edit
                            </Button>
                          ) : onReviewChanges ? (
                            <Button
                              variant="ghost"
                              size="compact"
                              onClick={() => onReviewChanges()}
                            >
                              Review
                            </Button>
                          ) : null}
                        </div>
                        <p className="mt-1 whitespace-pre-wrap text-sm">{note.body}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={selectedNote !== null} onOpenChange={(open) => !open && setSelectedNote(null)}>
        <DialogContent className="max-w-lg p-0">
          <DialogHeader className="border-b px-4 py-3">
            <DialogTitle>Edit Narrative Note</DialogTitle>
            <DialogDescription>
              Update this note using the same trade narrative controls as its source annotation.
            </DialogDescription>
            {onReviewChanges && selectedNote?.noteKind === "group" ? (
              <div className="pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const changeGroupId = selectedNote.changeGroupId;
                    const included = selectedNote.changeGroup?.included;
                    setSelectedNote(null);
                    onReviewChanges(changeGroupId, included);
                  }}
                >
                  Open Source Change
                </Button>
              </div>
            ) : null}
          </DialogHeader>
          {selectedNote?.noteKind === "group" && selectedNote.changeGroupId ? (
            <div className="max-h-[70dvh] min-h-96 overflow-hidden">
              <ChangeGroupNotesPanel
                changeGroupId={selectedNote.changeGroupId}
                included={selectedNote.changeGroup?.included ?? true}
                notes={selectedGroupNotes}
                showHeader={true}
                initialEditingNoteId={selectedNote._id as Id<"revPilotChangeGroupNotes">}
              />
            </div>
          ) : selectedNote && isNarrativeChangeNote(selectedNote) ? (
            <NarrativeChangeNoteEditForm
              note={selectedNote}
              onSave={handleUpdateChangeNote}
              onCancel={() => setSelectedNote(null)}
              isSaving={isSavingSelectedNote}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </>
  );
}

function RevPilotCombinedExportDialog({
  comparisonId,
  comparisonName,
  projectName,
  annotatedPdfUrl,
  downloadFromUrl,
}: {
  comparisonId: Id<"revPilotScheduleComparisons">;
  comparisonName: string;
  projectName?: string | null;
  annotatedPdfUrl?: string | null;
  downloadFromUrl: (url: string, filename: string) => Promise<void>;
}) {
  const canEdit = useCanEdit();
  const exportFilenames = useExportFilenames({ kind: "revPilotComparison", id: comparisonId });
  const names = { projectName, jobName: comparisonName };
  const reportTarget = exportTarget("revpilot_combined_pdf", {}, names);
  const annotatedTarget = exportTarget("revpilot_annotated_pdf", {}, names);
  const reportName = exportFilenames.resolve(reportTarget.key, reportTarget.defaultName);
  const annotatedName = exportFilenames.resolve(annotatedTarget.key, annotatedTarget.defaultName);
  const [deliverable, setDeliverable] = useState<"report" | "annotated">("report");
  const generateCombinedPdfExport = useMutation(api.revPilotExports.generateCombinedPdfExport);
  const { data: bridgeOptions } = useQuery(
    convexQuery(api.revPilotTakeoffBridge.getTakeoffBridgeOptions, { comparisonId })
  );
  const { data: latestExport } = useQuery(
    convexQuery(api.revPilotExports.getLatestCombinedPdfExport, { comparisonId })
  );
  const { data: latestExportDownloadUrl, refetch: refetchLatestExportDownloadUrl } = useQuery(
    convexQuery(api.revPilotExports.getCombinedPdfExportDownloadUrl, { comparisonId })
  );
  const [open, setOpen] = useState(false);
  useOnEditAccessLost(() => setOpen(false));
  const [includeTakeoffRevisions, setIncludeTakeoffRevisions] = useState(true);
  const [includeQuantityDeltas, setIncludeQuantityDeltas] = useState(true);
  const [includeTradeNarratives, setIncludeTradeNarratives] = useState(true);
  const [includeScheduleInPdf, setIncludeScheduleInPdf] = useState(true);
  const [includeTagsInPdf, setIncludeTagsInPdf] = useState(true);
  const [includeMeasurementsInPdf, setIncludeMeasurementsInPdf] = useState(true);
  const [pdfReportMode, setPdfReportMode] = useState<"executive_summary" | "full">("full");
  const [pdfExportIncludeColumns, setPdfExportIncludeColumns] = useState<string[]>([
    ...PDF_EXPORT_MEASUREMENT_COLUMNS,
  ]);
  const [isExporting, setIsExporting] = useState(false);
  const [autoDownloadArtifactId, setAutoDownloadArtifactId] = useState<string | null>(null);
  const autoDownloadInFlightRef = useRef<string | null>(null);
  // The name typed for a queued export, so the auto-download doesn't wait on the saved-name query.
  const autoDownloadNameRef = useRef<string | null>(null);

  const hasSelection = includeTakeoffRevisions || includeQuantityDeltas || includeTradeNarratives;
  const canExportTakeoff = Boolean(
    bridgeOptions?.linkedTakeoffJobIds.v1TakeoffJobId && bridgeOptions?.linkedTakeoffJobIds.v2TakeoffJobId
  );
  const isServerExportInProgress = latestExport?.status === "pending" || latestExport?.status === "processing";
  const canDownloadLatestExport = latestExport?.status === "completed" && Boolean(latestExportDownloadUrl);
  const columnSections = useMemo<MultiSelectSection[]>(
    () => [
      {
        id: "takeoff-columns",
        label: "Takeoff Columns",
        options: PDF_EXPORT_MEASUREMENT_COLUMNS.map((column) => ({ value: column, label: column })),
      },
    ],
    []
  );

  const handleSelectAllColumns = useCallback(() => {
    setPdfExportIncludeColumns([...PDF_EXPORT_MEASUREMENT_COLUMNS]);
  }, []);
  const handleDeselectAllColumns = useCallback(() => {
    setPdfExportIncludeColumns([]);
  }, []);

  const handleExport = async (fileName: string) => {
    if (deliverable === "annotated") {
      if (await handleDownloadAnnotatedPdf(fileName)) {
        await exportFilenames.saveBrowserExport("revpilot_annotated_pdf", {}, fileName);
        setOpen(false);
      }
      return;
    }
    if (!hasSelection) {
      toast.error("Select at least one section to export");
      return;
    }
    if (includeTakeoffRevisions && !canExportTakeoff) {
      toast.error("Link both Takeoff Pilot revisions before exporting TP revisions");
      return;
    }

    setIsExporting(true);
    try {
      const result = await generateCombinedPdfExport({
        comparisonId,
        options: {
          includeTakeoffRevisions,
          includeQuantityDeltas,
          includeTradeNarratives,
          takeoffOptions: {
            includeSchedule: includeScheduleInPdf,
            includeTags: includeTagsInPdf,
            includeMeasurements: includeMeasurementsInPdf,
            includeColumns: pdfExportIncludeColumns,
            reportMode: pdfReportMode,
          },
        },
      });
      autoDownloadNameRef.current = fileName;
      setAutoDownloadArtifactId(String(result.artifactId));
      await exportFilenames.saveForArtifact(result.artifactId, fileName);
      setOpen(false);
      toast.success("Preparing RevPilot combined PDF export. Download will start when ready.");
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to export RevPilot report");
      console.error(error);
    } finally {
      setIsExporting(false);
    }
  };

  const handleDownload = useCallback(async (fileName = reportName) => {
    try {
      const { data: refreshedUrl } = await refetchLatestExportDownloadUrl();
      const downloadUrl = refreshedUrl ?? latestExportDownloadUrl;
      if (!downloadUrl) {
        throw new Error("Failed to get download URL");
      }
      await downloadFromUrl(downloadUrl, withExportExtension(fileName, "pdf"));
      toast.success("Download started");
      return true;
    } catch (error) {
      notifyError(error, "Failed to download RevPilot export");
      console.error(error);
      return false;
    }
  }, [downloadFromUrl, latestExportDownloadUrl, refetchLatestExportDownloadUrl, reportName]);

  useEffect(() => {
    if (!autoDownloadArtifactId) return;
    if (!latestExport || String(latestExport._id) !== autoDownloadArtifactId) return;

    if (latestExport.status === "failed") {
      toast.error(latestExport.error ?? "RevPilot export failed");
      setAutoDownloadArtifactId(null);
      return;
    }

    if (latestExport.status !== "completed" || !latestExportDownloadUrl) return;
    if (autoDownloadInFlightRef.current === autoDownloadArtifactId) return;

    autoDownloadInFlightRef.current = autoDownloadArtifactId;
    void (async () => {
      try {
        await handleDownload(autoDownloadNameRef.current ?? undefined);
      } catch (error) {
        console.error("Failed to auto-download RevPilot export:", error);
      } finally {
        autoDownloadInFlightRef.current = null;
        setAutoDownloadArtifactId(null);
      }
    })();
  }, [
    autoDownloadArtifactId,
    handleDownload,
    latestExport?._id,
    latestExport?.error,
    latestExport?.status,
    latestExportDownloadUrl,
  ]);

  const handleDownloadAnnotatedPdf = async (fileName: string) => {
    if (!annotatedPdfUrl) {
      return false;
    }
    try {
      await downloadFromUrl(annotatedPdfUrl, withExportExtension(fileName, "pdf"));
      toast.success("Download started");
      return true;
    } catch (error) {
      notifyError(error, "Failed to download annotated PDF");
      console.error(error);
      return false;
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-3">
        <ExportButton onClick={() => setOpen(true)} busy={isServerExportInProgress} />
        {isServerExportInProgress ? (
          <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" /> Preparing export...
          </span>
        ) : latestExport?.status === "failed" ? (
          <span className="text-sm text-destructive">{latestExport.error ?? "Export failed"}</span>
        ) : null}
      </div>
      <ExportDialog
        open={open}
        onOpenChange={setOpen}
        description={comparisonName}
        deliverables={[
          { value: "report", label: "Revision Report" },
          { value: "annotated", label: "Annotated Plans" },
        ]}
        deliverable={deliverable}
        onDeliverableChange={setDeliverable}
        extension="pdf"
        defaultFileName={deliverable === "report" ? reportName : annotatedName}
        optionsSummary={deliverable === "report" && !hasSelection ? "No Sections Selected" : undefined}
        options={
          deliverable === "report" ? (
            <>
              <ExportOptionGroup title="Sections">
                <ExportOptionCheckbox checked={includeTakeoffRevisions} onCheckedChange={setIncludeTakeoffRevisions}>
                  TP Revisions
                </ExportOptionCheckbox>
                <ExportOptionCheckbox checked={includeQuantityDeltas} onCheckedChange={setIncludeQuantityDeltas}>
                  Quantity Deltas
                </ExportOptionCheckbox>
                <ExportOptionCheckbox checked={includeTradeNarratives} onCheckedChange={setIncludeTradeNarratives}>
                  Trade Narratives
                </ExportOptionCheckbox>
              </ExportOptionGroup>
              {includeTakeoffRevisions ? (
                <ExportOptionGroup title="Takeoff PDF Options">
                  <Select value={pdfReportMode} onValueChange={(value) => setPdfReportMode(value as "executive_summary" | "full")}>
                    <SelectTrigger aria-label="Report mode">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="full">Full - include measurement detail</SelectItem>
                      <SelectItem value="executive_summary">Executive Summary - summary rows only</SelectItem>
                    </SelectContent>
                  </Select>
                  <ExportOptionCheckbox checked={includeScheduleInPdf} onCheckedChange={setIncludeScheduleInPdf}>
                    Include schedule pages
                  </ExportOptionCheckbox>
                  <ExportOptionCheckbox checked={includeTagsInPdf} onCheckedChange={setIncludeTagsInPdf}>
                    Include tag callouts on plans
                  </ExportOptionCheckbox>
                  <ExportOptionCheckbox checked={includeMeasurementsInPdf} onCheckedChange={setIncludeMeasurementsInPdf}>
                    Include measurement markup on plans
                  </ExportOptionCheckbox>
                  <MultiSelect
                    sections={columnSections}
                    selected={pdfExportIncludeColumns}
                    onSelect={(column) => setPdfExportIncludeColumns((previous) => previous.includes(column) ? previous : [...previous, column])}
                    onRemove={(column) => setPdfExportIncludeColumns((previous) => previous.filter((candidate) => candidate !== column))}
                    onSelectAll={handleSelectAllColumns}
                    onDeselectAll={handleDeselectAllColumns}
                    placeholder="Include columns..."
                    triggerWidth="w-full"
                  />
                </ExportOptionGroup>
              ) : null}
            </>
          ) : undefined
        }
        lastFile={
          deliverable === "report"
            ? lastFileFromArtifact({
                artifact: latestExport,
                canDownload: canDownloadLatestExport,
                download: handleDownload,
                saveName: (fileName) =>
                  latestExport ? exportFilenames.saveForArtifact(latestExport._id, fileName) : Promise.resolve(),
              })
            : null
        }
        notice={
          deliverable === "annotated"
            ? annotatedPdfUrl
              ? null
              : "Annotated plans appear here once the comparison report is ready."
            : !canEdit
              ? "Reviewers can download the last report but can't generate a new one."
              : includeTakeoffRevisions && !canExportTakeoff
                ? "Link both Takeoff Pilot revisions before exporting TP revisions."
                : null
        }
        onExport={handleExport}
        isExporting={isExporting}
        exportDisabled={
          deliverable === "annotated"
            ? !annotatedPdfUrl
            : !canEdit ||
              !hasSelection ||
              isServerExportInProgress ||
              (includeTakeoffRevisions && !canExportTakeoff)
        }
      />
    </>
  );
}

// Keeps review-mode and completed-mode selection controls together so both screens stay consistent.
// eslint-disable-next-line complexity
function PlanSetReviewView({
  comparisonId,
  completedMode = false,
  highlightedGroupId = null,
  highlightedGroupIncluded = null,
  onBackToSummary,
}: {
  comparisonId: Id<"revPilotScheduleComparisons">;
  completedMode?: boolean;
  highlightedGroupId?: Id<"revPilotChangeGroups"> | null;
  highlightedGroupIncluded?: boolean | null;
  onBackToSummary?: () => void;
}) {
  const navigate = useAppNavigate();
  // Including, excluding, and completing a review are project writes; reviewers browse changes.
  const canEdit = useCanEdit();
  const [showExcluded, setShowExcluded] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedDocumentPairId, setSelectedDocumentPairId] = useState("all");

  const { data: comparisonDetails } = useQuery(convexQuery(api.scheduleComparisons.getWithDetails, {
    comparisonId,
  }));
  const recordActionLog = useRecordActionLog();

  const { data: changeGroups } = useQuery(convexQuery(api.changeGroups.listByComparison, {
    comparisonId,
    included: showExcluded ? false : true,
    ...(selectedDocumentPairId !== "all"
      ? { documentPairId: selectedDocumentPairId as Id<"revPilotDocumentPairs"> }
      : {}),
  }));
  const { data: notes } = useQuery(convexQuery(api.changeGroups.listNotesByComparison, {
    comparisonId,
  }));

  const { data: selectionsSummary } = useQuery(convexQuery(api.changeGroups.getSelectionsSummary, {
    comparisonId,
  }));

  // Get PDF URL for the modal viewer
  const { data: reportPdfUrl } = useQuery(convexQuery(
    api.scheduleComparisons.getStorageUrl,
    comparisonDetails?.comparison?.reportPdfFileKey
        ? { fileKey: comparisonDetails.comparison.reportPdfFileKey }
        : "skip"
  ));

  const updateGroupSelection = useMutation(api.changeGroups.updateGroupSelection);
  const updateAllGroupSelections = useMutation(api.changeGroups.updateAllGroupSelections);
  const confirmSelections = useMutation(api.changeGroups.confirmGroupSelections);

  const [isConfirming, setIsConfirming] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  useOnEditAccessLost(() => setShowConfirmDialog(false));

  useEffect(() => {
    if (completedMode && highlightedGroupIncluded !== null) {
      setShowExcluded(!highlightedGroupIncluded);
    }
  }, [completedMode, highlightedGroupIncluded]);

  useEffect(() => {
    if (comparisonDetails?.comparison) {
      const comp = comparisonDetails.comparison;
      if (!completedMode && comp.reviewStatus === "completed") {
        navigate({ to: "/revPilot/$comparisonId", params: { comparisonId } });
        return;
      }
    }
  }, [comparisonDetails, comparisonId, completedMode, navigate]);

  useEffect(() => {
    if (!highlightedGroupId || !changeGroups) {
      return;
    }
    const target = document.getElementById(`change-group-${highlightedGroupId}`);
    target?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [changeGroups, highlightedGroupId]);

  const handleToggleGroup = useCallback(
    async (groupId: Id<"revPilotChangeGroups">, included: boolean) => {
      try {
        await updateGroupSelection({ groupId, included });
        await recordActionLog();
        toast.success(included ? "Change included" : "Change excluded");
      } catch (error) {
        notifyError(error, "Failed to update selection");
        console.error(error);
      }
    },
    [recordActionLog, updateGroupSelection]
  );

  const handleIncludeAll = useCallback(async () => {
    try {
      await updateAllGroupSelections({
        comparisonId,
        included: true,
        ...(selectedDocumentPairId !== "all"
          ? { documentPairId: selectedDocumentPairId as Id<"revPilotDocumentPairs"> }
          : {}),
      });
      await recordActionLog();
      toast.success(selectedDocumentPairId === "all" ? "All changes included" : "Visible document changes included");
    } catch (error) {
      notifyError(error, "Failed to update selections");
      console.error(error);
    }
  }, [comparisonId, recordActionLog, selectedDocumentPairId, updateAllGroupSelections]);

  const handleExcludeAll = useCallback(async () => {
    try {
      await updateAllGroupSelections({
        comparisonId,
        included: false,
        ...(selectedDocumentPairId !== "all"
          ? { documentPairId: selectedDocumentPairId as Id<"revPilotDocumentPairs"> }
          : {}),
      });
      await recordActionLog();
      toast.success(selectedDocumentPairId === "all" ? "All changes excluded" : "Visible document changes excluded");
    } catch (error) {
      notifyError(error, "Failed to update selections");
      console.error(error);
    }
  }, [comparisonId, recordActionLog, selectedDocumentPairId, updateAllGroupSelections]);

  const handleConfirmReview = useCallback(async () => {
    setIsConfirming(true);
    try {
      await confirmSelections({ comparisonId });
      await recordActionLog();
      toast.success("Review completed");
      if (completedMode) onBackToSummary?.();
      else navigate({ to: "/revPilot/$comparisonId", params: { comparisonId } });
    } catch (error) {
      notifyError(error, "Failed to complete review");
      console.error(error);
    } finally {
      setIsConfirming(false);
      setShowConfirmDialog(false);
    }
  }, [comparisonId, completedMode, confirmSelections, navigate, onBackToSummary, recordActionLog]);

  if (!comparisonDetails || !changeGroups || !selectionsSummary) {
    return (
      <div className="flex items-center justify-center min-h-dvh">
        <ProcessingSpinner message="Loading changes..." />
      </div>
    );
  }

  const { comparison } = comparisonDetails;
  const documentPairs = comparisonDetails.documentPairs ?? [];
  const selectedDocumentPair = documentPairs.find((pair) => String(pair._id) === selectedDocumentPairId);
  const reportPdfDisplayName = selectedDocumentPair?.displayName ??
    (documentPairs.length === 1 ? documentPairs[0]?.displayName : comparison.name) ??
    "Annotated plan set";
  const reportPdfSource = reportPdfUrl === undefined
    ? undefined
    : {
        fileUrl: reportPdfUrl ?? null,
        filename: reportPdfDisplayName,
      };
  const notesByGroupId = new Map<string, RevPilotNote[]>();
  for (const note of notes ?? []) {
    const groupNotes = notesByGroupId.get(String(note.changeGroupId)) ?? [];
    groupNotes.push(note);
    notesByGroupId.set(String(note.changeGroupId), groupNotes);
  }

  // An in-progress review is the comparison page itself. Reviewing a completed comparison is a
  // sub-view of its summary, so the comparison becomes the parent crumb.
  const showSummaryCrumb = completedMode && onBackToSummary !== undefined;
  const reviewTrail: Crumb[] = [{ label: "RevPilot", href: "/revPilot" }];
  if (showSummaryCrumb) {
    reviewTrail.push({
      label: comparison.name,
      href: "/revPilot/$comparisonId",
      params: { comparisonId: String(comparisonId) },
      onClick: (event) => {
        // The summary is the same route; swap views instead of navigating.
        event.preventDefault();
        onBackToSummary();
      },
    });
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title={showSummaryCrumb ? "Review Changes" : comparison.name}
        trail={reviewTrail}
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPdfModal(true)}
              disabled={!reportPdfUrl}
            >
              <Eye />
              View Full PDF
            </Button>
            {!canEdit ? null : completedMode ? (
              <>
                {showExcluded && selectionsSummary.excluded > 0 ? (
                  <Button variant="outline" onClick={handleIncludeAll} size="sm">
                    <Check />
                    Reinclude Visible
                  </Button>
                ) : null}
                {!showExcluded && selectionsSummary.included > 0 ? (
                  <Button variant="outline" onClick={handleExcludeAll} size="sm">
                    <X />
                    Exclude Visible
                  </Button>
                ) : null}
                <Button onClick={() => setShowConfirmDialog(true)} size="sm" disabled={isConfirming}>
                  Complete Review
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={handleIncludeAll} size="sm">
                  <Check />
                  Include All
                </Button>
                <Button variant="outline" onClick={handleExcludeAll} size="sm">
                  <X />
                  Exclude All
                </Button>
                <Button onClick={() => setShowConfirmDialog(true)} size="sm">
                  Complete Review
                </Button>
              </>
            )}
          </>
        }
      />

      <div
        role="toolbar"
        aria-label="Review filters"
        className="flex flex-wrap items-center gap-x-4 gap-y-2"
      >
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <span className="flex items-center gap-1.5">
            <CountBadge value={selectionsSummary.included} />
            Included
          </span>
          <span className="flex items-center gap-1.5">
            <CountBadge value={selectionsSummary.excluded} />
            Excluded
          </span>
          <span className="flex items-center gap-1.5">
            <CountBadge value={selectionsSummary.total} />
            Total
          </span>
        </div>
        {documentPairs.length > 0 ? (
          <Select value={selectedDocumentPairId} onValueChange={setSelectedDocumentPairId}>
            <SelectTrigger
              className="h-8 w-[220px] max-w-full"
              aria-label="Filter changes by document"
            >
              <SelectValue placeholder="All documents" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All documents</SelectItem>
              {documentPairs.map((pair) => (
                <SelectItem key={pair._id} value={String(pair._id)}>
                  {pair.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : null}
        <div className="ms-auto flex items-center gap-2">
          <Label htmlFor="plan-set-review-show-excluded" className="text-sm font-normal text-muted-foreground">
            {showExcluded ? "Showing excluded items" : "Showing included items"}
          </Label>
          <Switch
            id="plan-set-review-show-excluded"
            checked={showExcluded}
            onCheckedChange={setShowExcluded}
            aria-label="Toggle excluded items view"
          />
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        {!canEdit
          ? "Review each source change and its notes."
          : completedMode
            ? "Review source changes, reinclude excluded items, and add or edit trade narrative notes."
            : "Review each change and select whether to include or exclude it from the next step."}
      </p>

      {changeGroups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-4">
            <p className="text-muted-foreground">
              {showExcluded
                ? "No excluded changes."
                : "No included changes. Toggle the switch above to see excluded items."}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {changeGroups.map((group) => (
            <ChangeGroupCard
              key={group._id}
              group={group}
              highlighted={highlightedGroupId === group._id}
              readOnlySelection={!canEdit}
              viewMode={showExcluded ? "excluded" : "included"}
              notes={notes ? notesByGroupId.get(String(group._id)) ?? [] : undefined}
              onToggle={(included) => handleToggleGroup(group._id, included)}
            />
          ))}
        </div>
      )}

      {showConfirmDialog && (
        <ConfirmReviewDialog
          summary={selectionsSummary}
          onConfirm={handleConfirmReview}
          onCancel={() => setShowConfirmDialog(false)}
          isLoading={isConfirming}
        />
      )}

      <Dialog open={showPdfModal} onOpenChange={setShowPdfModal}>
        <DialogContent className="h-[95dvh] max-h-[95dvh] w-[95vw] max-w-[95vw] overflow-hidden p-0 sm:max-w-[95vw]">
          <DialogTitle className="sr-only">Annotated Plan Set PDF</DialogTitle>
          <PdfModalPipViewport
            sourcePdf={reportPdfSource}
            sourceId={String(comparison.reportPdfFileKey ?? comparisonId)}
            className="h-full min-h-0 rounded-none border-0"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}

interface ChangeGroupCardProps {
  group: {
    _id: Id<"revPilotChangeGroups">;
    pageNumber: number;
    included: boolean;
    regionCount: number;
    totalAreaPx: number;
    bbox: { x: number; y: number; width: number; height: number };
    v1CropFileKey?: string;
    v2CropFileKey?: string;
  };
  viewMode: "included" | "excluded";
  notes: RevPilotNote[] | undefined;
  onToggle: (included: boolean) => void;
  highlighted?: boolean;
  readOnlySelection?: boolean;
}

function ChangeGroupCard({
  group,
  viewMode,
  notes,
  onToggle,
  highlighted = false,
  readOnlySelection = false,
}: ChangeGroupCardProps) {
  const { data: v1CropUrl } = useQuery(convexQuery(api.changeGroups.getGroupWithImages, {
    groupId: group._id,
  }));
  const [fullscreenImage, setFullscreenImage] = useState<{ url: string; label: string } | null>(null);

  const isViewingExcluded = viewMode === "excluded";

  return (
    <Card
      id={`change-group-${group._id}`}
      className={cn("scroll-mt-6 overflow-hidden", highlighted && "ring-2 ring-primary")}
    >
      <div className="grid gap-0 xl:grid-cols-[1fr_minmax(320px,400px)]">
        {/* Left side - Images area */}
        <div className="flex flex-col">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <CardTitle className="text-base">
                  Page {group.pageNumber}
                </CardTitle>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{group.regionCount} region{group.regionCount !== 1 ? "s" : ""}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {readOnlySelection ? (
                  <Badge variant="outline" size="sm">{group.included ? "Included" : "Excluded"}</Badge>
                ) : isViewingExcluded ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onToggle(true)}
                  >
                    <Check />
                    Include
                  </Button>
                ) : (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => onToggle(false)}
                  >
                    <X />
                    Exclude
                  </Button>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="flex-1">
            {v1CropUrl ? (
              <div className="grid gap-4 md:grid-cols-2">
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground mb-2">Before (v1)</p>
                  {v1CropUrl.v1CropUrl ? (
                    <button
                      type="button"
                      onClick={() => setFullscreenImage({ url: v1CropUrl.v1CropUrl!, label: "Before (v1)" })}
                      className="flex w-full cursor-zoom-in justify-center rounded border bg-background"
                      aria-label="View Before (v1) image fullscreen"
                    >
                      <img
                        src={v1CropUrl.v1CropUrl}
                        alt="Before"
                        className="max-h-[34rem] w-auto max-w-full object-contain hover:opacity-90 transition-opacity"
                      />
                    </button>
                  ) : (
                    <div className="flex h-72 items-center justify-center rounded border bg-muted">
                      <span className="text-muted-foreground text-sm">Image not available</span>
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground mb-2">After (v2)</p>
                  {v1CropUrl.v2CropUrl ? (
                    <button
                      type="button"
                      onClick={() => setFullscreenImage({ url: v1CropUrl.v2CropUrl!, label: "After (v2)" })}
                      className="flex w-full cursor-zoom-in justify-center rounded border bg-background"
                      aria-label="View After (v2) image fullscreen"
                    >
                      <img
                        src={v1CropUrl.v2CropUrl}
                        alt="After"
                        className="max-h-[34rem] w-auto max-w-full object-contain hover:opacity-90 transition-opacity"
                      />
                    </button>
                  ) : (
                    <div className="flex h-72 items-center justify-center rounded border bg-muted">
                      <span className="text-muted-foreground text-sm">Image not available</span>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
          </CardContent>
        </div>

        {/* Right side - Full height notes panel */}
        <div className="border-t xl:border-t-0 xl:border-l bg-muted/20">
          <ChangeGroupNotesPanel 
            changeGroupId={group._id} 
            included={group.included} 
            notes={notes} 
            showHeader={true}
          />
        </div>
      </div>

      {/* Fullscreen Image Modal */}
      <Dialog open={fullscreenImage !== null} onOpenChange={() => setFullscreenImage(null)}>
        <DialogContent className="max-w-[98vw] max-h-[98dvh] w-auto h-auto min-w-[50vw] min-h-[50dvh] p-0 bg-black/95 border-none">
          <DialogHeader className="px-6 py-4 border-b border-white/10 flex-shrink-0">
            <DialogTitle className="text-white">{fullscreenImage?.label}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 flex items-center justify-center overflow-auto p-6">
            {fullscreenImage && (
              <img
                src={fullscreenImage.url}
                alt={fullscreenImage.label}
                className="max-w-full max-h-[calc(98dvh-120px)] object-contain"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

interface ConfirmReviewDialogProps {
  summary: { total: number; included: number; excluded: number } | undefined;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

function ConfirmReviewDialog({ summary, onConfirm, onCancel, isLoading }: ConfirmReviewDialogProps) {
  if (!summary) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>Confirm Review</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            You're about to complete the review. Here's a summary:
          </p>
          <div className="flex gap-4 justify-center">
            <div className="text-center">
              <div className="text-2xl font-bold text-status-completed">{summary.included}</div>
              <div className="text-xs text-muted-foreground">Included</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-status-error">{summary.excluded}</div>
              <div className="text-xs text-muted-foreground">Excluded</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold">{summary.total}</div>
              <div className="text-xs text-muted-foreground">Total</div>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={onConfirm} disabled={isLoading}>
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin" />
                  Confirming...
                </>
              ) : (
                "Confirm"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export function ComparisonDetailPage() {
  const comparisonId = useRouteParam("comparisonId");
  const typedComparisonId = comparisonId as Id<"revPilotScheduleComparisons">;
  const { data: details } = useQuery(convexQuery(api.scheduleComparisons.getWithDetails, {
    comparisonId: typedComparisonId,
  }));
  return (
    <ActionLogProvider
      projectId={details?.comparison.projectId ?? null}
      pilot="RevPilot"
      targetKind="revPilotComparison"
      targetId={comparisonId}
    >
      <ComparisonDetailContent />
    </ActionLogProvider>
  );
}

function ComparisonDetailContent() {
  const comparisonId = useRouteParam("comparisonId");
  const typedComparisonId = comparisonId as Id<"revPilotScheduleComparisons">;
  const navigate = useAppNavigate();

  const { data: details } = useQuery(convexQuery(api.scheduleComparisons.getWithDetails, {
    comparisonId: typedComparisonId,
  }));
  const comparison = details?.comparison ?? null;
  const comparisonKind = normalizeComparisonKind(comparison?.comparisonKind);
  const { data: pageProgress } = useQuery(
    convexQuery(
      api.planSetComparisonPages.getProgress,
      comparisonKind === "plan_set"
        ? { comparisonId: typedComparisonId }
        : "skip"
    )
  );
  const removeComparison = useMutation(api.scheduleComparisons.remove);
  const [isDeleting, setIsDeleting] = useState(false);
  const [reviewChangeGroupId, setReviewChangeGroupId] = useState<Id<"revPilotChangeGroups"> | null>(null);
  const [reviewChangeGroupIncluded, setReviewChangeGroupIncluded] = useState<boolean | null>(null);
  const [showCompletedReviewChanges, setShowCompletedReviewChanges] = useState(false);
  const [narrativeSelectedLabel, setNarrativeSelectedLabel] = useState("all");
  // Call all hooks first (required by React rules)
  const { reportJsonUrl, reportPdfUrl } = useComparisonReportUrls(comparison);
  const reportJsonUrlForNarrative = comparisonKind === "schedule" ? reportJsonUrl : null;
  const { report, reportLoading, reportError } = useComparisonReport(
    comparison?.status,
    reportJsonUrlForNarrative
  );

  const downloadFromUrl = useCallback(async (url: string, filename: string) => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to download ${filename}`);
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  }, []);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await removeComparison({ comparisonId: typedComparisonId });
      toast.success("Comparison deleted");
      void navigate({ to: "/revPilot" });
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to delete comparison");
    } finally {
      setIsDeleting(false);
    }
  };

  // Loading states
  if (details === undefined) {
    return (
      <div className="space-y-4">
        <PageHeaderSkeleton withTrail />
        <Card>
          <ProcessingSpinner message="Loading comparison details..." />
        </Card>
      </div>
    );
  }

  if (details === null || !comparison) {
    return (
      <div className="space-y-4">
        <PageHeader
          title="Comparison Not Found"
          trail={[{ label: "RevPilot", href: "/revPilot" }]}
        />
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Comparison not found</AlertTitle>
          <AlertDescription>The comparison you requested does not exist.</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (
    isPlanSetReviewOpen(
      comparisonKind,
      comparison.status,
      comparison.reviewStatus,
      showCompletedReviewChanges
    )
  ) {
    const shouldUsePdfChangeLayerReview =
      comparison.reviewWorkflow === "pdf_change_layer" ||
      (comparison.reviewWorkflow !== "group_image" && (details.documentPairs?.length ?? 0) > 0);

    if (shouldUsePdfChangeLayerReview) {
      return (
        <PdfChangeReviewView
          comparisonId={typedComparisonId}
          details={details}
          completedMode={showCompletedReviewChanges}
          onBackToSummary={() => {
            setShowCompletedReviewChanges(false);
            setReviewChangeGroupId(null);
            setReviewChangeGroupIncluded(null);
          }}
        />
      );
    }

    return (
      <PlanSetReviewView
        comparisonId={typedComparisonId}
        completedMode={showCompletedReviewChanges}
        highlightedGroupId={reviewChangeGroupId}
        highlightedGroupIncluded={reviewChangeGroupIncluded}
        onBackToSummary={() => {
          setShowCompletedReviewChanges(false);
          setReviewChangeGroupId(null);
          setReviewChangeGroupIncluded(null);
        }}
      />
    );
  }

  // Otherwise, show the regular detail view (for schedule or completed plan_set)
  const v1Tables = (details.v1Tables ?? []) as ExtractedTableRef[];
  const v2Tables = (details.v2Tables ?? []) as ExtractedTableRef[];

  return (
    <div className="space-y-4">
      <PageHeader
        title={comparison.name}
        trail={[{ label: "RevPilot", href: "/revPilot" }]}
        meta={
          <>
            <Badge variant="outline" size="sm">
              {comparisonKind === "plan_set" ? "Plan Set" : "Schedule"}
            </Badge>
            <ComparisonStatusBadge status={comparison.status} />
          </>
        }
      />

      {comparisonKind === "schedule" && (
        <RevisionTablePreviewList
          title="Extracted Tables"
          subtitle={`${v1Tables.length} tables from v1 · ${v2Tables.length} tables from v2`}
          tables={[...v1Tables, ...v2Tables]}
        />
      )}

      {(comparison.status === "extracting" || comparison.status === "processing") && (
        <Card>
          <CardHeader>
            <CardTitle>RevPilot is processing</CardTitle>
            <CardDescription>
              This page will update automatically when the comparison is ready.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ProcessingSpinner
              message={getComparisonProcessingMessage(comparison.status, comparisonKind)}
              className="py-10"
            />
            {comparisonKind === "plan_set" && pageProgress ? (
              <PlanSetPageProgress
                completed={pageProgress.completed}
                total={pageProgress.total}
              />
            ) : null}
          </CardContent>
        </Card>
      )}

      {comparison.status === "completed" && comparisonKind === "plan_set" ? (
        <CompletedPlanSetReportSection
          comparisonId={typedComparisonId}
          comparison={comparison}
          projectName={details.project?.name}
          reportPdfUrl={reportPdfUrl}
          downloadFromUrl={downloadFromUrl}
          narrativeSelectedLabel={narrativeSelectedLabel}
          onNarrativeSelectedLabelChange={setNarrativeSelectedLabel}
          onReviewChanges={(changeGroupId, included) => {
            setReviewChangeGroupId(changeGroupId || null);
            setReviewChangeGroupIncluded(included ?? null);
            setShowCompletedReviewChanges(true);
          }}
        />
      ) : comparison.status === "completed" && isScheduleReport(report) ? (
        <CompletedReportSection
          report={report}
          reportLoading={reportLoading}
          reportError={reportError}
          stats={comparison.stats}
        />
      ) : null}

      {comparison.status === "failed" && comparison.error && (
        <Alert variant="destructive">
          <AlertCircle className="size-4" />
          <AlertTitle>Comparison failed</AlertTitle>
          <AlertDescription>{comparison.error}</AlertDescription>
          <RetryPlanSetComparisonButton
            comparisonId={typedComparisonId}
            comparisonKind={comparisonKind}
          />
        </Alert>
      )}

      <div className="flex items-center justify-end">
        <RequiresCapability mode="hide">
          <DeleteConfirmationDialog
            title="Delete Comparison"
            description={`Are you sure you want to delete "${comparison.name}"? This action cannot be undone.`}
            onConfirm={handleDelete}
            disabled={isDeleting}
          >
            <Button variant="destructive" disabled={isDeleting}>
              <Trash2 />
              Delete Comparison
            </Button>
          </DeleteConfirmationDialog>
        </RequiresCapability>
      </div>
    </div>
  );
}

function CompletedPlanSetReportSection({
  comparisonId,
  comparison,
  projectName,
  reportPdfUrl,
  downloadFromUrl,
  narrativeSelectedLabel,
  onNarrativeSelectedLabelChange,
  onReviewChanges,
}: {
  comparisonId: Id<"revPilotScheduleComparisons">;
  comparison: {
    name: string;
    reportPdfFileKey?: string;
    overallSummary?: string;
    stats?: {
      changedRows: number;
    };
  };
  projectName?: string | null;
  reportPdfUrl: string | null | undefined;
  downloadFromUrl: (url: string, filename: string) => Promise<void>;
  narrativeSelectedLabel: string;
  onNarrativeSelectedLabelChange: (label: string) => void;
  onReviewChanges: (changeGroupId?: Id<"revPilotChangeGroups">, included?: boolean) => void;
}) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Report Exports</CardTitle>
          <CardDescription>
            Create a combined PDF export or download the available plan-set reports.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <RevPilotCombinedExportDialog
              comparisonId={comparisonId}
              comparisonName={comparison.name}
              projectName={projectName}
              annotatedPdfUrl={reportPdfUrl}
              downloadFromUrl={downloadFromUrl}
            />
          </div>

          {comparison.overallSummary && (
            <div className="rounded-md border bg-muted/20 p-3">
              <p className="text-sm font-medium">Summary</p>
              <p className="text-sm text-muted-foreground">{comparison.overallSummary}</p>
            </div>
          )}
        </CardContent>
      </Card>
      <RevPilotTakeoffBridge comparisonId={comparisonId} />
      <NarrativeSummarySection
        comparisonId={comparisonId}
        selectedLabel={narrativeSelectedLabel}
        onSelectedLabelChange={onNarrativeSelectedLabelChange}
        onReviewChanges={onReviewChanges}
      />
      <QuantityDeltaSection comparisonId={comparisonId} />
    </div>
  );
}

export const CompletedReportSection = CompletedScheduleReportSection;
